import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, from, of } from 'rxjs';
import { map, catchError, concatMap, last, switchMap, tap } from 'rxjs/operators';
import * as XLSX from 'xlsx';
import { ApiServiceService } from './api-service.service';
import { SectionInfo, FormQuestions, OptionValues } from '../Models/Question-details';
import { FormConditionalLogic } from '../Models/ConditionalLogic-data.model';
import { DocumentParserService, TextLine } from './document-parser.service';

// ============================================================
// Interfaces
// ============================================================

export interface ParsedField {
  name: string;
  label: string;
  type: string;
  required: boolean;
  options?: string[];
  helpText?: string;
  minLength?: number;
  maxLength?: number;
  expression?: number;
  // CCRA-style metadata (only set when parseStructuredFormat detects a
  // scoping-question + control-sub-table workbook).
  identifierGroup?: string;   // e.g. 'FCI', 'CUI', 'SI', 'ICT', 'Risk', or '' for controls
  isControl?: boolean;        // true when this row is part of the control sub-table
  numberPart?: string;        // original Number column value, e.g. '1', '1.a', '3.1.1'
  // True when this row is HIDDEN in the source workbook (the macro hides
  // dependent rows until answered). This is the authoritative initial-visibility
  // signal — a hidden row starts collapsed and is revealed by its parent answer.
  initiallyHidden?: boolean;
}

export interface ParsedSection {
  title: string;
  description?: string;
  fields: ParsedField[];
}

export interface ParsedConditionalRule {
  sourceFieldName: string;
  triggerValue: string;
  targetFieldName: string;
  skipToFieldName?: string;
  // CCRA-style: explicit set of field names that become mandatory when the
  // source field is answered with triggerValue. When undefined, generateConditionalLogic
  // falls back to the legacy "direct sub-letter children" derivation.
  mandatoryDeps?: string[];
  // Best-effort: the dependants are revealed when the source is answered with
  // ANY value (used for non-Yes/No parent questions, e.g. a dropdown whose
  // sub-questions should appear once it's answered). generateConditionalLogic
  // then emits one reveal condition per option instead of a single trigger.
  revealOnAnyAnswer?: boolean;
}

export interface ParseResult {
  success: boolean;
  sections: ParsedSection[];
  totalFields: number;
  errors: string[];
  warnings: string[];
  conditionalRules: ParsedConditionalRule[];
}

export interface ImportProgress {
  currentSection: number;
  totalSections: number;
  sectionName: string;
  status: 'pending' | 'saving' | 'success' | 'error';
  message?: string;
}

export interface FullImportResult {
  success: boolean;
  sectionsCreated: number;
  fieldsCreated: number;
  conditionalRulesCreated: number;
  errors: string[];
}

export interface EditSaveResult {
  success: boolean;
  sectionsSaved: number;
  sectionsDeleted: number;
  errors: string[];
}

// ============================================================
// Type mapping: friendly name → API Type
// ============================================================

const TYPE_MAP: Record<string, string> = {
  'text':               'TextInput',
  'textbox':            'TextInput',
  'text entry':         'TextInput',
  'numeric':            'TextInput',
  'numeric entry':      'TextInput',
  'number':             'TextInput',
  'textarea':           'TextArea',
  'long text':          'TextArea',
  'multi-line':         'TextArea',
  'multiline':          'TextArea',
  'radio':              'RadioButton',
  'radiobutton':        'RadioButton',
  'radio button':       'RadioButton',
  'true/false':         'RadioButton',
  'yes/no':             'RadioButton',
  'checkbox':           'CheckBox',
  'check':              'CheckBox',
  'checkboxes':         'CheckBox',
  'dropdown':           'DropDownListSelection',
  'select':             'DropDownListSelection',
  'selection':          'DropDownListSelection',
  'dropdown selection': 'DropDownListSelection',
  'date':               'Date',
  'datepicker':         'Date',
  'date entry':         'Date',
  'date picker':        'Date',
  'file':               'Attachment',
  'fileupload':         'Attachment',
  'file upload':        'Attachment',
  'attachment':         'Attachment',
  'label':              'TextLabel',
  'text label':         'TextLabel',
  'guidance':           'GuidanceText',
  'guidance text':      'GuidanceText',
};

// Expression IDs for validation (matches regularExpressions from API)
const EXPRESSION_MAP: Record<string, number> = {
  'email': 9,
  'phone': 10,
  'cage':  11,
  'duns':  13,
  'ssn':   15,
  'naics': 16,
};

@Injectable({
  providedIn: 'root'
})
export class ExcelImportService {

  constructor(
    private apiService: ApiServiceService,
    private documentParser: DocumentParserService
  ) {}

  // ============================================================
  // PUBLIC: Parse any supported file (.xlsx, .xls, .docx, .pdf)
  // ============================================================

  parseFile(file: File): Observable<ParseResult> {
    const name = file.name.toLowerCase();

    if (name.endsWith('.xlsx') || name.endsWith('.xls') || name.endsWith('.xlsm')) {
      return this.parseExcelFile(file);
    }

    if (name.endsWith('.docx') || name.endsWith('.doc') || name.endsWith('.pdf')) {
      return this.parseDocumentFile(file);
    }

    return of({
      success: false, sections: [], totalFields: 0,
      errors: [`Unsupported file type: ${file.name}. Supported: .xlsx, .xls, .xlsm, .docx, .pdf`],
      warnings: [], conditionalRules: []
    });
  }

  // ============================================================
  // PUBLIC: Parse Excel file
  // ============================================================

  parseExcelFile(file: File): Observable<ParseResult> {
    return from(this.readExcelFile(file)).pipe(
      map(workbook => this.parseWorkbook(workbook)),
      catchError(error => of({
        success: false,
        sections: [],
        totalFields: 0,
        errors: [`Failed to parse Excel file: ${error.message}`],
        warnings: [],
        conditionalRules: []
      }))
    );
  }

  // ============================================================
  // PUBLIC: Parse Word/PDF file
  // ============================================================

  parseDocumentFile(file: File): Observable<ParseResult> {
    return this.documentParser.parseFile(file).pipe(
      map(({ lines, errors }) => {
        const result: ParseResult = {
          success: true, sections: [], totalFields: 0,
          errors: [...errors], warnings: [], conditionalRules: []
        };

        if (lines.length === 0) {
          result.success = false;
          result.errors.push('No text content found in document');
          return result;
        }

        // Use the style-aware parser directly so we keep indent, isBold, and PDF
        // widget metadata (dropdowns / radios / dates from the annotation layer).
        this.parseTextWithStyles(lines, result);

        this.detectConditionalRules(result);

        return result;
      }),
      catchError(error => of({
        success: false, sections: [], totalFields: 0,
        errors: [`Failed to parse document: ${error.message}`],
        warnings: [], conditionalRules: []
      }))
    );
  }

  // ============================================================
  // PUBLIC: Full import — saves sections + conditional logic + localStorage
  // ============================================================

  fullImport(
    sections: ParsedSection[],
    conditionalRules: ParsedConditionalRule[],
    templateId: string,
    progressCallback?: (progress: ImportProgress) => void
  ): Observable<FullImportResult> {

    if (!sections.length) {
      return of({
        success: false, sectionsCreated: 0, fieldsCreated: 0,
        conditionalRulesCreated: 0, errors: ['No sections to import']
      });
    }

    // Step 1: Build all SectionInfo objects up front (so we have stable UUIDs)
    const builtSections: SectionInfo[] = sections.map((s, i) =>
      this.convertToSectionInfo(s, templateId, i)
    );

    // Step 2: Build conditional logic from the parsed rules + built sections.
    // Initial visibility comes from the source workbook's row-hidden state: a row
    // hidden in the macro starts hidden (chart node mandatory:false) and is
    // revealed when its parent is answered; visible rows show on load. We do NOT
    // inject display:none — that only affects field-level rendering and would hide
    // a dependant's input even after it's revealed.
    // Reconcile the AI's conditional-rule field references to the ACTUAL field names.
    // The model sometimes formats a name differently on the field vs. in the rule
    // (case, underscores, a "q"/"status" affix), which makes the exact-name match in
    // generateConditionalLogic silently no-op — the dependant then never starts hidden
    // and shows on load. Rewrite each rule reference to the real field name.
    this.reconcileRuleNames(sections, conditionalRules);

    const hiddenNames = new Set<string>();
    for (const s of sections) {
      for (const f of s.fields) if (f.initiallyHidden) hiddenNames.add(f.name);
    }
    // A field that a rule REVEALS is, by definition, a dependant that must start
    // hidden — otherwise "reveal" has nothing to reveal and the dependant filter
    // drops it. Derive the hidden set from the rules too, so conditional logic works
    // even when the model didn't explicitly flag every revealed field initiallyHidden.
    for (const r of conditionalRules) {
      if (r.targetFieldName) hiddenNames.add(r.targetFieldName);
      for (const d of (r.mandatoryDeps || [])) hiddenNames.add(d);
    }

    // GUARD: a field may only START hidden if some rule can REVEAL it. The model
    // sometimes over-flags initiallyHidden on top-level scoping questions (e.g. Q3, Q4)
    // that nothing reveals — which would strand them hidden forever. Keep hidden only
    // the names that are a target / mandatory-dependant of a conditional rule; everything
    // else shows on load.
    const revealable = new Set<string>();
    for (const r of conditionalRules) {
      if (r.targetFieldName) revealable.add(r.targetFieldName);
      for (const d of (r.mandatoryDeps || [])) revealable.add(d);
    }
    for (const n of Array.from(hiddenNames)) {
      if (!revealable.has(n)) hiddenNames.delete(n);
    }

    const conditionalLogic = this.generateConditionalLogic(templateId, builtSections, conditionalRules, hiddenNames);

    // Number every question label by its hierarchy (13., 13.1, 13.3.1 …) so the
    // sub-question ↔ main-question link is visible to the user.
    this.applyHierarchicalLabelNumbers(builtSections, conditionalRules);

    const result: FullImportResult = {
      success: true, sectionsCreated: 0, fieldsCreated: 0,
      conditionalRulesCreated: conditionalRules.length, errors: []
    };

    // Step 3: Save sections sequentially (FRESH import only — a template with no
    // existing form). Re-imports/edits go through the GUID-reusing merge path so
    // SaveSection UPDATES existing sections instead of creating duplicates.
    return from(builtSections).pipe(
      concatMap((section, index) => {
        if (progressCallback) {
          progressCallback({
            currentSection: index + 1,
            totalSections: builtSections.length,
            sectionName: section.Title,
            status: 'saving'
          });
        }

        return this.apiService.SaveSection(section).pipe(
          tap(() => {
            if (progressCallback) {
              progressCallback({
                currentSection: index + 1,
                totalSections: builtSections.length,
                sectionName: section.Title,
                status: 'success'
              });
            }
            result.sectionsCreated++;
            result.fieldsCreated += section.Fields.length;
          }),
          catchError(error => {
            if (progressCallback) {
              progressCallback({
                currentSection: index + 1,
                totalSections: builtSections.length,
                sectionName: section.Title,
                status: 'error',
                message: error.message
              });
            }
            result.errors.push(`Failed to save "${section.Title}": ${error.message}`);
            return of(null);
          })
        );
      }),
      last(), // wait for ALL sections to finish
      // Step 4: Always save conditional logic (even empty — API needs the section structure)
      switchMap(() => {
        return this.apiService.SaveConditionalLogic(conditionalLogic).pipe(
          catchError(error => {
            result.errors.push(`Failed to save conditional logic: ${error.message}`);
            return of(null);
          })
        );
      }),
      // Step 4b: Auto-populate the Attribute Mapper — gated by Flag 1
      // (isAiFormBuilderEnabled): the attribute mapper is part of the AI Form
      // Builder, so skip it when that flag is off.
      switchMap(() => {
        let builderOn = false;
        try { builderOn = localStorage.getItem('aiFormBuilderEnabled') === 'true'; } catch { builderOn = false; }
        if (!builderOn) return of(null);
        const attrPayload = this.buildAttributeMappings(builtSections, templateId, conditionalRules);
        if (!attrPayload.Attributes.length) return of(null);
        return this.apiService.SaveAttributeDataMap(attrPayload).pipe(
          catchError(error => {
            result.errors.push(`Failed to save attribute mappings: ${error.message}`);
            return of(null);
          })
        );
      }),
      // Step 4b: Auto-populate the Attribute Mapper — gated by Flag 1
      // (isAiFormBuilderEnabled): the attribute mapper is part of the AI Form
      // Builder, so skip it when that flag is off.
      switchMap(() => {
        let builderOn = false;
        try { builderOn = localStorage.getItem('aiFormBuilderEnabled') === 'true'; } catch { builderOn = false; }
        if (!builderOn) return of(null);
        const attrPayload = this.buildAttributeMappings(builtSections, templateId, conditionalRules);
        if (!attrPayload.Attributes.length) return of(null);
        return this.apiService.SaveAttributeDataMap(attrPayload).pipe(
          catchError(error => {
            result.errors.push(`Failed to save attribute mappings: ${error.message}`);
            return of(null);
          })
        );
      }),
      // Step 5: Update localStorage
      map(() => {
        localStorage.setItem('AllQuestions', JSON.stringify([{ Section: builtSections }]));
        localStorage.setItem('ConditionalLogicData', JSON.stringify(conditionalLogic));
        result.success = result.errors.length === 0;
        return result;
      })
    );
  }

  // ============================================================
  // PUBLIC: REPLACE import — the uploaded file becomes the WHOLE form.
  // ============================================================
  // A file (re)import is a clean REPLACE: clear conditional logic, DELETE every
  // section currently on the template (fetched from the BACKEND — the source of
  // truth, which localStorage may not reflect), then fullImport the new file.
  // This is the only way to remove old sections that a stale localStorage-based
  // merge can't see. On a fresh template GetSections returns nothing, so it's just
  // a normal import. (In-place, GUID-preserving editing is the Form Assistant path,
  // not re-upload.)
  replaceImport(
    sections: ParsedSection[],
    conditionalRules: ParsedConditionalRule[],
    templateId: string,
    progressCallback?: (progress: ImportProgress) => void
  ): Observable<FullImportResult> {
    if (!sections.length) {
      return of({
        success: false, sectionsCreated: 0, fieldsCreated: 0,
        conditionalRulesCreated: 0, errors: ['No sections to import']
      });
    }

    // 1) Clear the template's conditional logic first (replace-all) — removes stale
    //    logic and avoids any delete being blocked by a rule referencing a section.
    const emptyLogic = this.generateConditionalLogic(templateId, [], [], new Set());
    return this.apiService.SaveConditionalLogic(emptyLogic).pipe(
      catchError(() => of(null)),
      // 2) Delete EVERY existing section on this template (backend truth).
      switchMap(() => this.deleteAllSections(templateId)),
      // 3) Fresh import of the uploaded file.
      switchMap(() => this.fullImport(sections, conditionalRules, templateId, progressCallback))
    );
  }

  // Delete every section currently saved for this template (from the backend).
  private deleteAllSections(templateId: string): Observable<any> {
    return this.apiService.GetSections().pipe(
      catchError(() => of(null)),
      switchMap((resp: any) => {
        const arr: any[] = Array.isArray(resp) ? resp : (resp?.Data || resp?.data || []);
        const ids = (arr || [])
          .filter((s: any) => s && s.UniqueID && (!s.TemplateID || s.TemplateID === templateId))
          .map((s: any) => s.UniqueID);
        if (!ids.length) return of(null);

        const userId = localStorage.getItem('UserId') || '';
        return from(ids).pipe(
          concatMap((id: string) => this.apiService.DeleteSection({
            UniqueID: id,
            UpdatedBy: { Name: '', Email: '', UPN: '', UniqueID: userId },
          }).pipe(catchError(() => of(null)))),
          last(),
          map(() => null)
        );
      })
    );
  }

  // ============================================================
  // PRIVATE: Build attribute mappings (one unique AttributeName per question)
  // ============================================================
  // Mirrors the manual Attribute Mapper payload (SaveAttributeDataMap):
  //   { TemplateID, FormID, Attributes: [{ AttributeValue=field.UniqueID, AttributeName, UniqueId }] }
  //
  // AttributeName is a HYBRID, HIERARCHICAL name: a dotted outline number + the
  // question's descriptive slug, e.g.
  //   Q1_legal_company_name        (top-level question 1)
  //   Q1.1_duns_number             (sub-question revealed by Q1)
  //   Q1.1.1_duns_issue_date       (sub-question revealed by Q1.1)
  //   Q1.2_cage_code               (second sub-question of Q1)
  //   Q2_primary_contact_name      (top-level question 2)
  // This stays readable no matter how large the form grows, and the number makes
  // each attribute's place in the question tree obvious. The parent→child
  // relationships come straight from the conditional rules a rule REVEALS its
  // target / mandatory-dependants, so those are sub-questions of the rule source.
  // A numeric suffix is appended only if a slug collision somehow occurs.
  // ============================================================
  // PRIVATE: Compute PER-SECTION outline numbers (CCRA-style) from the rules.
  // ============================================================
  // Numbering restarts in every section: each section's main questions are
  // 1, 2, 3…; a question REVEALED by another (its rule target / mandatory
  // dependant) is a sub-question and gets parent.N — recursively (2.1, 2.1.1).
  // Shared by the attribute mapper AND the question labels so both show the SAME
  // hierarchy. A field whose parent lives in a different section is treated as a
  // top-level question of its own section.
  private computeFieldNumbers(
    sections: SectionInfo[],
    rules: ParsedConditionalRule[] = []
  ): Map<string, string> {
    const sectionOf = new Map<string, number>();
    sections.forEach((s, i) => { for (const f of s.Fields) sectionOf.set(f.Name, i); });
    const nameSet = new Set(sectionOf.keys());

    // Content-only blocks (intro / instructions / legal notices) are not questions —
    // they carry no number and must not consume a position in the 1,2,3 sequence.
    const contentBlocks = new Set<string>();
    for (const s of sections)
      for (const f of s.Fields)
        // Content blocks are saved as TextInput (see convertToSectionInfo) but flagged
        // with __contentBlock; exclude them from numbering so their rich QuestionHtml is
        // not overwritten by the label-renumber pass and they get no "1." prefix.
        if ((f as any).__contentBlock || (f as any).Type === 'Info' || (f as any).Type === 'GuidanceText' || (f as any).Type === 'TextLabel') contentBlocks.add(f.Name);

    // parent → child (first parent wins), then break any cycle.
    const parentOf = new Map<string, string>();
    for (const rule of (rules || [])) {
      const parent = rule.sourceFieldName;
      if (!parent || !nameSet.has(parent)) continue;
      const kids = [rule.targetFieldName, ...(rule.mandatoryDeps || [])];
      for (const child of kids) {
        if (!child || child === parent || !nameSet.has(child)) continue;
        if (parentOf.has(child)) continue;
        parentOf.set(child, parent);
      }
    }
    const createsCycle = (child: string, parent: string): boolean => {
      let p: string | undefined = parent;
      const seen = new Set<string>();
      while (p) {
        if (p === child || seen.has(p)) return true;
        seen.add(p);
        p = parentOf.get(p);
      }
      return false;
    };
    for (const [child, parent] of [...parentOf.entries()]) {
      if (createsCycle(child, parent)) parentOf.delete(child);
    }

    // children in document order.
    const childrenOf = new Map<string, string[]>();
    for (const s of sections) {
      for (const f of s.Fields) {
        const p = parentOf.get(f.Name);
        if (p) { if (!childrenOf.has(p)) childrenOf.set(p, []); childrenOf.get(p)!.push(f.Name); }
      }
    }

    const numberByName = new Map<string, string>();
    const assign = (name: string, num: string, secIdx: number) => {
      numberByName.set(name, num);
      let k = 0;
      for (const kid of (childrenOf.get(name) || [])) {
        if (sectionOf.get(kid) !== secIdx) continue;   // keep numbering within the section
        if (numberByName.has(kid)) continue;
        assign(kid, `${num}.${++k}`, secIdx);
      }
    };

    sections.forEach((s, i) => {
      let root = 0;
      for (const f of s.Fields) {
        if (contentBlocks.has(f.Name)) continue;   // content blocks are unnumbered
        if (numberByName.has(f.Name)) continue;
        const p = parentOf.get(f.Name);
        // A sub-question of an in-section parent is numbered by that parent's walk.
        if (p && sectionOf.get(p) === i) continue;
        assign(f.Name, `${++root}`, i);   // top-level question of this section
      }
      // Safety net: anything still unnumbered in this section → next top-level.
      for (const f of s.Fields) {
        if (contentBlocks.has(f.Name)) continue;
        if (!numberByName.has(f.Name)) assign(f.Name, `${++root}`, i);
      }
    });

    return numberByName;
  }

  // ============================================================
  // PRIVATE: Prefix each question's visible Label with its dotted hierarchy
  // number ("13.", "13.1", "13.3.1" …) so the sub-question ↔ main-question
  // connection is obvious to the end user. Idempotent — re-running strips the
  // old number first, so labels never accumulate "13. 13. …".
  // ============================================================
  private applyHierarchicalLabelNumbers(
    sections: SectionInfo[],
    rules: ParsedConditionalRule[] = []
  ): void {
    const numberByName = this.computeFieldNumbers(sections, rules);

    for (const s of sections) {
      for (const f of s.Fields) {
        const num = numberByName.get(f.Name);
        if (!num) continue;
        // Strip an existing leading number ("13. ", "13.1. ", "5) ", and letter-suffixed
        // forms like "1a. ", "1.a. ", "3.a. ") so re-numbering is idempotent and the
        // document's own number isn't doubled with our outline number ("1.1. 1a. …" →
        // "1.1. …"). Trailing punctuation + space is REQUIRED so a real label that just
        // starts with a number ("7 years retention", "3TG metals") is left intact.
        const baseLabel = (f.Label || '')
          .replace(/^\s*\d+(?:[.\)]\d+)*(?:\.?[a-z])?[.\):]\s+/i, '')
          .trim();
        f.Label = baseLabel ? `${num}. ${baseLabel}` : `${num}.`;
        f.QuestionHtml = this.generateQuestionHtml(f);   // label changed → regen
      }
    }
  }

  private buildAttributeMappings(
    builtSections: SectionInfo[],
    templateId: string,
    conditionalRules: ParsedConditionalRule[] = []
  ): any {
    let formId = 0;
    try { formId = Number(JSON.parse(localStorage.getItem('FormId') ?? '0')) || 0; } catch { formId = 0; }

    // Flatten the mappable fields in document order + remember each field's SECTION
    // number — these are the only fields that get an attribute name.
    const mappable: { name: string; field: any }[] = [];
    const sectionNoByName = new Map<string, number>();
    builtSections.forEach((section, si) => {
      for (const field of section.Fields) {
        sectionNoByName.set(field.Name, si + 1);
        if (field.IsMappable) mappable.push({ name: field.Name, field });
      }
    });

    // Per-section hierarchy numbers (1, 1.1, 2.1.1 …) — same as the question labels.
    const numberByName = this.computeFieldNumbers(builtSections, conditionalRules);

    // Build the payload. CRITICAL: attribute names must be GLOBALLY UNIQUE — the
    // Attribute Mapper rejects duplicates and disables Save. Labels restart per
    // section (so two sections both have a "1."), so we QUALIFY the attribute name
    // with the section number → "Q{section}.{perSectionNumber}" (Q1.1, Q2.1,
    // Q3.1.1). That stays unique across the whole form while still showing the
    // section + position. Deduped as a final safety net.
    const used = new Set<string>();
    const attributes: any[] = [];
    for (const { name, field } of mappable) {
      const perSec = numberByName.get(name) || '';
      const sec = sectionNoByName.get(name) || 1;
      const num = `${sec}.${perSec}`;   // section-qualified → globally unique
      const rawSlug = (field.Name || 'field').trim() || 'field';
      // The field's own Name may ALREADY carry a question number (e.g. the AI named
      // it "Q9_denied_party_screening", "Q9_1_denied_party_lists", or just "Q1").
      // Strip that leading number token to get the descriptive remainder; we
      // re-attach our own qualified number below.
      const slug = rawSlug.replace(/^Q\d+(?:[._]\d+)*/i, '').replace(/^[._]+/, '');
      // If a descriptive part remains → "Q1.1_certificate_number"; if the name was
      // ONLY a number → just "Q1.1" (no doubled-up token).
      let attrName = slug ? `Q${num}_${slug}` : `Q${num}`;
      let n = 2;
      while (used.has(attrName.toLowerCase())) {
        attrName = slug ? `Q${num}_${slug}_${n++}` : `Q${num}_${n++}`;
      }
      used.add(attrName.toLowerCase());

      attributes.push({
        AttributeValue: field.UniqueID,
        AttributeName: attrName,
        UniqueId: this.generateUUID(),
      });
    }

    return { TemplateID: templateId, FormID: formId, Attributes: attributes };
  }

  // ============================================================
  // PUBLIC: Live-form AI editing — convert an ALREADY-SAVED form into the
  // ParseResult shape EditFormAI consumes. This lets a user edit a form they
  // already imported ("I forgot a question") by giving the AI an instruction,
  // instead of re-uploading (which would duplicate everything).
  // ============================================================
  // The saved form is SectionInfo[] (from localStorage `AllQuestions` /
  // GetSections), each carrying its real UniqueID. We strip it down to the
  // editable ParseResult; the GUIDs are re-attached on the way back in
  // mergeEditedForm so SaveSection UPDATES in place rather than inserting.
  sectionsToParseResult(sections: SectionInfo[]): ParseResult {
    const result: ParseResult = {
      success: true, sections: [], totalFields: 0,
      errors: [], warnings: [], conditionalRules: []
    };

    for (const sec of (sections || [])) {
      const ps: ParsedSection = {
        title: sec.Title || 'Section',
        description: (sec as any).Description || '',
        fields: []
      };
      for (const fq of ((sec.Fields as any[]) || [])) {
        const options = ((fq.Options as any[]) || [])
          .map(o => typeof o === 'string' ? o : (o?.OptionText ?? o?.OptionValue ?? ''))
          .filter((o: string) => !!o);
        const pf: ParsedField = {
          name: fq.Name,
          label: fq.Label,
          // Pass the canonical backend type straight through — resolveQuestionType
          // round-trips it losslessly on the way back.
          type: fq.Type,
          required: !!fq.Required,
          helpText: fq.HelpContent || '',
          expression: fq.Expression || 0,
        };
        if (options.length > 0) pf.options = options;
        ps.fields.push(pf);
        result.totalFields++;
      }
      result.sections.push(ps);
    }
    // conditionalRules intentionally left empty: a field/question edit must not
    // regenerate (and therefore risk wiping) the existing saved conditional logic.
    // The AI only adds rules when the instruction explicitly asks for logic.
    return result;
  }

  // ============================================================
  // Field-name numbering parser (shared) — splits a field name into a main number +
  // optional sub-identifier, handling EVERY variant the model emits so hierarchy
  // detection never needs per-example patching:
  //   PARENT (sub=null):  q1 · Q1 · 1 · q1_response · q1_fci_clause · Q12
  //   CHILD  (sub=a/1):   q1a · Q1_a · Q1.a · q1a_response · q2b_ssp · 1.a · q5_1
  // Rules: optional q/Q, digits = main, optional single "_"/"." separator, then a SINGLE
  // letter or digits ONLY when a boundary (end/"_"/".") follows (so "_response" is never
  // a sub); no leading number → not a Q-field (vendor_name, control_3_1_1 → null).
  // ============================================================
  private parseFieldNumber(name: string): { main: string; sub: string | null } | null {
    const s = (name || '').trim();
    const head = /^q?(\d+)/i.exec(s);
    if (!head) return null;
    const rest = s.slice(head[0].length);
    const sub = /^[._]?([a-z]|\d+)(?=[_.]|$)/i.exec(rest);
    return { main: head[1], sub: sub ? sub[1].toLowerCase() : null };
  }

  // ============================================================
  // PUBLIC: Build the AUTHORITATIVE conditional-rule set for an imported form.
  // ============================================================
  // The name-hierarchy derive is DETERMINISTIC (same field structure → same reveals);
  // the LLM's own conditionalRules vary run-to-run (count, direction, spurious scoping
  // chains). To keep imports PREDICTABLE **without ever dropping a genuine rule**, the
  // derived hierarchy rules are the stable backbone: take ALL of them, then merge EVERY
  // AI rule EXCEPT the two things that are provably garbage and caused the 16-vs-9 swing:
  //   1. exact duplicates (same source→target reveal we already have), and
  //   2. parent→parent "scoping chains" — both ends are top-level scoping questions and
  //      the TARGET is itself a parent that owns children (e.g. q2→q3). These wrongly hide
  //      a scoping question that must stay visible, and are pure per-run noise.
  // Anything else the model emits — a reverse "reveal on No", an extra dependency, a
  // cross-question reveal names alone can't infer — is KEPT. So a real conditional is
  // never lost; only duplicate/contradictory scoping noise is discarded.
  buildConditionalRules(sections: ParsedSection[], aiRules: ParsedConditionalRule[] = []): ParsedConditionalRule[] {
    const derived = this.deriveConditionalRulesFromNames(sections);
    const norm = (s: string) => (s || '').trim().toLowerCase();

    // Map each scoping-parent main → its derived rule, so we can tell whether an AI rule
    // is already covered by the bundle (same target revealed on the same trigger). The
    // key set doubles as "mains the hierarchy owns" for the parent→parent chain check.
    const derivedByMain = new Map<string, ParsedConditionalRule>();
    for (const r of derived) {
      const p = this.parseFieldNumber(r.sourceFieldName);
      if (p) derivedByMain.set(p.main, r);
    }

    // TRIGGER OVERRIDE (reverse-trigger fix): the name-hierarchy derive can only GUESS the
    // trigger is "Yes"/"True" — it can't tell a reveal-on-"No" gateway apart ("If No: 1a…").
    // The AI reads the document, so when an AI rule targets the SAME parent's child with a
    // DIFFERENT trigger, adopt the AI's trigger onto the derived bundle. Without this, a
    // "reveal on No" wrongly fires on "Yes" (Q1a/Q1b showing when Q1 = Yes).
    for (const r of (aiRules || [])) {
      const s = this.parseFieldNumber(r.sourceFieldName);
      if (!s) continue;
      const d = derivedByMain.get(s.main);
      if (!d) continue;
      const targetsBundle =
        norm(d.targetFieldName) === norm(r.targetFieldName) ||
        (d.mandatoryDeps || []).some(n => norm(n) === norm(r.targetFieldName));
      if (!targetsBundle) continue;
      if (r.revealOnAnyAnswer) {
        d.revealOnAnyAnswer = true;
        d.triggerValue = '';
      } else if (norm(r.triggerValue) && norm(r.triggerValue) !== norm(d.triggerValue)) {
        d.triggerValue = r.triggerValue;   // e.g. adopt "No" over the guessed "Yes"
        d.revealOnAnyAnswer = undefined;
      }
    }

    const result = [...derived];
    const seen = new Set(result.map(r => (r.sourceFieldName + '>' + r.targetFieldName).toLowerCase()));
    for (const r of (aiRules || [])) {
      const key = (r.sourceFieldName + '>' + r.targetFieldName).toLowerCase();
      if (seen.has(key)) continue;                       // (1) exact duplicate — already covered

      const src = this.parseFieldNumber(r.sourceFieldName);
      const tgt = this.parseFieldNumber(r.targetFieldName);

      // (2) parent→parent scoping chain: source & target are both top-level scoping
      // questions and the target owns children → drops q2→q3 / q3→q4 that hid a scoping Q.
      const isScopingChain = !!src && !!tgt && src.sub == null && tgt.sub == null && derivedByMain.has(tgt.main);
      if (isScopingChain) continue;

      // (3) redundant per-child rule: the AI split a parent's reveals into one rule per
      // child (q2→q2b, q2→q2c). If the derived bundle for that parent ALREADY reveals this
      // target on the SAME trigger, the child is covered — drop the extra so the count
      // stays deterministic. A DIFFERENT trigger (e.g. reveal-on-"No") is a genuine nuance
      // names can't infer → keep it, nothing lost.
      if (src) {
        const d = derivedByMain.get(src.main);
        if (d) {
          const covered = (d.mandatoryDeps || []).some(n => norm(n) === norm(r.targetFieldName));
          const sameTrigger = norm(d.triggerValue) === norm(r.triggerValue) || !norm(r.triggerValue);
          if (covered && sameTrigger) continue;
        }
      }

      result.push(r); seen.add(key);                     // everything else is a genuine rule — keep it
    }
    return result;
  }

  // ============================================================
  // PUBLIC: Collapse consecutive information-only sections into ONE.
  // ============================================================
  // Many source documents open with several paragraphs of preamble — an intro,
  // "how to use this form", regulatory notes, a scoring overview, etc. The model
  // tends to emit ONE section per heading, so the form starts with a stack of
  // sections that each hold a single Info block (no inputs) — visually noisy and,
  // to the user, clearly "one intro" that got split up.
  //
  // This merges any run of ADJACENT info-only sections into a single section:
  //   • Document-agnostic — keyed on structure (a section whose every field is an
  //     Info block), never on specific titles/content, so it works for any file.
  //   • 0% content loss — every Info block is kept; each merged-away section's title
  //     is folded into its block as a heading line (only when not already present),
  //     so no heading text is dropped and nothing is fabricated.
  //   • A real input section between two info sections is a hard boundary, so
  //     unrelated parts of the form are never fused.
  mergeConsecutiveInfoSections(sections: ParsedSection[]): ParsedSection[] {
    const isInfo = (f: ParsedField) => /^info$/i.test((f.type || '').trim());
    const infoOnly = (s: ParsedSection) =>
      !!s && Array.isArray(s.fields) && s.fields.length > 0 && s.fields.every(isInfo);

    const out: ParsedSection[] = [];
    let run: ParsedSection[] = [];

    // Info content renders as HTML ([innerHTML]) downstream, so a plain-text block gets
    // its newlines turned into <br>; a block that already contains HTML is kept verbatim.
    const looksHtml = (s: string) => /<[a-z][\s\S]*?>/i.test(s || '');
    const toHtml = (s: string) => (looksHtml(s) ? (s || '') : (s || '').replace(/\n/g, '<br>'));

    const flush = () => {
      if (run.length === 0) return;
      const head = run[0];
      const first = head.fields[0];

      // A lone single-Info section is already a single block — leave it untouched.
      const totalFields = run.reduce((n, s) => n + s.fields.length, 0);
      if (run.length === 1 && totalFields === 1) { out.push(run[0]); run = []; return; }

      // Otherwise COMBINE every Info block in the run into ONE Info field so the intro
      // reads as a single block, not a stack of separate field entries. Each merged-away
      // section's heading is folded in (as bold) so no title text is lost, and any
      // per-field helpText is preserved — 0% content loss, nothing fabricated.
      const blocks: string[] = [];
      for (let i = 0; i < run.length; i++) {
        const sec = run[i];
        const title = (sec.title || '').trim();
        sec.fields.forEach((f, idx) => {
          const label = (f.label || '');
          if (i > 0 && idx === 0 && title && !label.toLowerCase().includes(title.toLowerCase())) {
            blocks.push('<b>' + title + '</b>');
          }
          if (label.trim()) blocks.push(toHtml(label));
          if (f.helpText && f.helpText.trim()) blocks.push(toHtml(f.helpText));
        });
      }

      const combined: ParsedField = {
        ...first,
        name: first.name || 'intro_info',
        type: first.type || 'Info',
        label: blocks.join('<br><br>'),
      };
      delete (combined as any).helpText;

      out.push({ title: head.title, description: head.description, fields: [combined] });
      run = [];
    };

    for (const s of (sections || [])) {
      if (infoOnly(s)) run.push(s);
      else { flush(); out.push(s); }
    }
    flush();
    return out;
  }

  // ============================================================
  // PUBLIC: Fallback — derive reveal rules from the field-name hierarchy.
  // ============================================================
  // The AI is asked to emit conditionalRules, but sometimes misses them (or names
  // them so they get pruned by the backend guardrail). When that leaves a form
  // with NO rules, we reconstruct the obvious Yes/gateway reveals from the AI's own
  // numbering: a parent "Q5_*" with children "Q5_1_*", "Q5_2_*" → reveal the
  // children when Q5 is answered (trigger = its Yes/True option, else any answer).
  //
  // Limitation: reverse-trigger reveals ("show 11.1 when Q11 = No") can't be known
  // from names alone — those still need the AI to detect them or a quick Edit-AI
  // tweak ("reveal Q11.1 when Q11 = No").
  deriveConditionalRulesFromNames(sections: ParsedSection[]): ParsedConditionalRule[] {
    const fields: ParsedField[] = [];
    for (const s of (sections || [])) for (const f of s.fields) fields.push(f);

    // Recognise a parent scoping question and its sub-questions from the AI's numbering.
    // Handles BOTH conventions:
    //   • letter-suffix (CCRA / CMMC):  q1_response → q1a_response, q2b_response  (sub = a/b)
    //   • numeric-suffix (legacy):      q5 → q5_1, q5.1                          (sub = 1/2)
    // One comprehensive parser for EVERY numbering variant the model emits, so this
    // never needs per-example patching again. Splits a field name into a main number
    // and an optional sub-identifier:
    //
    //   PARENT  (sub = null):  q1 · Q1 · 1 · q1_response · Q2 · 12
    //   CHILD   (sub = a/1):   q1a · Q1_a · Q1.a · q1a_response · 1.a · 1a
    //                          q2b_response · Q3_e · q10_a · q5_1 · q5.1 · Q12_a
    //
    // Rules:
    //   • optional leading "q"/"Q"
    //   • a run of digits = the main number
    //   • optional single separator ("_" or ".")
    //   • sub = a SINGLE letter OR a run of digits, and ONLY if a boundary (end / "_" / ".")
    //     follows — so a descriptive suffix like "_response" is never mistaken for a sub
    //   • no leading number at all (e.g. "vendor_name", "control_3_1_1") → not a Q-field
    const parentByMain = new Map<string, ParsedField>();
    const childrenByMain = new Map<string, ParsedField[]>();
    for (const f of fields) {
      const p = this.parseFieldNumber(f.name);
      if (!p) continue;
      if (p.sub == null) {
        if (!parentByMain.has(p.main)) parentByMain.set(p.main, f);
      } else {
        if (!childrenByMain.has(p.main)) childrenByMain.set(p.main, []);
        childrenByMain.get(p.main)!.push(f);
      }
    }

    const rules: ParsedConditionalRule[] = [];
    for (const [main, children] of childrenByMain) {
      const parent = parentByMain.get(main);
      if (!parent || !children.length) continue;
      const yesOpt = (parent.options || []).find(o => /^(yes|true)\b/i.test((o || '').trim()));
      rules.push({
        sourceFieldName: parent.name,
        triggerValue: yesOpt || '',
        targetFieldName: children[0].name,
        mandatoryDeps: children.map(c => c.name),
        revealOnAnyAnswer: yesOpt ? undefined : true,
      });
    }
    return rules;
  }

  // ============================================================
  // PUBLIC: Merge an AI-edited ParseResult back onto the live form, REUSING the
  // existing UniqueIDs so the save updates in place (no duplicate sections).
  // ============================================================
  // Matching is by section Title and, within a section, by field Name (both
  // case-insensitive). Matched → keep the saved GUID (SaveSection UPDATES).
  // New → fresh GUID (SaveSection INSERTS). Present-before-but-gone-now sections
  // are returned in deletedSectionIds for the caller to DeleteSection.
  mergeEditedForm(
    existing: SectionInfo[],
    edited: ParseResult,
    templateId: string
  ): { sections: SectionInfo[]; deletedSectionIds: string[]; newFieldIds: string[] } {
    const norm = (s: string) => (s || '').trim().toLowerCase();

    const existingByTitle = new Map<string, any>();
    for (const s of (existing || [])) existingByTitle.set(norm(s.Title), s);

    // Global field lookup by name (across ALL sections). Used so a field MOVED to
    // a different section keeps its saved GUID — preserving its identity (and any
    // attribute mapping / conditional-logic wiring) instead of being recreated.
    const globalFieldByName = new Map<string, any>();
    for (const s of (existing || [])) {
      for (const f of ((s.Fields as any[]) || [])) {
        if (!globalFieldByName.has(norm(f.Name))) globalFieldByName.set(norm(f.Name), f);
      }
    }

    // Track which existing field GUIDs we re-use, so a field that moved isn't also
    // matched again, and section identity can fall back to shared fields.
    const usedSectionIds = new Set<string>();
    // GUIDs of genuinely NEW fields (no existing match) — so callers can auto-map
    // ONLY the new questions in the Attribute Mapper and leave the rest alone.
    const newFieldIds: string[] = [];

    const built: SectionInfo[] = edited.sections.map((ps, si) => {
      // Match the section by title; if the title changed, fall back to the existing
      // (still-unused) section that shares the most field names — so a small title
      // tweak doesn't delete-and-recreate the whole section (losing GUIDs).
      let prevSec: any = existingByTitle.get(norm(ps.title));
      if (!prevSec) {
        const editedNames = new Set(ps.fields.map(f => norm(f.name)));
        let best: any = null, bestShared = 0;
        for (const s of (existing || [])) {
          if (s.UniqueID && usedSectionIds.has(s.UniqueID)) continue;
          if (existingByTitle.get(norm(ps.title))) continue;
          let shared = 0;
          for (const f of ((s.Fields as any[]) || [])) if (editedNames.has(norm(f.Name))) shared++;
          if (shared > bestShared) { bestShared = shared; best = s; }
        }
        if (best && bestShared > 0) prevSec = best;
      }
      const sectionInfo = new SectionInfo();
      sectionInfo.UniqueID = prevSec?.UniqueID || this.generateUUID();
      if (prevSec?.UniqueID) usedSectionIds.add(prevSec.UniqueID);
      sectionInfo.TemplateID = templateId;
      sectionInfo.Title = ps.title;
      sectionInfo.Description = ps.description || prevSec?.Description || '';
      // Order follows the EDITED form's section order (si), not the old sequence —
      // this is what lets the AI reorder sections (Section 1 ↔ Section 2 …). The
      // section GUID is still reused (matched above), so it's a move, not a copy.
      sectionInfo.Sequence = si;
      sectionInfo.DisplayType = prevSec?.DisplayType || 'One';
      sectionInfo.CreatedBy = prevSec?.CreatedBy || { UniqueID: localStorage.getItem('UserId') || '' };
      sectionInfo.UpdatedBy = { UniqueID: localStorage.getItem('UserId') || '' };

      const prevFieldsByName = new Map<string, any>();
      for (const f of ((prevSec?.Fields as any[]) || [])) prevFieldsByName.set(norm(f.Name), f);

      sectionInfo.Fields = ps.fields.map((field, index) => {
        // Prefer a match within this section; else reuse the same-named field from
        // ANY section (a move) so its GUID — and identity — is preserved.
        const prevField: any =
          prevFieldsByName.get(norm(field.name)) || globalFieldByName.get(norm(field.name));
        const fq = new FormQuestions();
        fq.UniqueID = prevField?.UniqueID || this.generateUUID();
        if (!prevField) newFieldIds.push(fq.UniqueID);   // brand-new question
        fq.Name = field.name;
        fq.Label = field.label;
        fq.Type = this.resolveQuestionType(field.type);
        fq.Required = field.required;
        fq.IsMappable = prevField?.IsMappable ?? true;
        fq.DisplayOrder = index + 1;
        fq.HelpContent = field.helpText || '';
        this.applyGuidance(fq, field.helpText);   // guidance block (manual shape)
        fq.Expression = field.expression || 0;
        fq.MinChar = field.minLength ?? prevField?.MinChar ?? null;
        fq.MaxChar = field.maxLength ?? prevField?.MaxChar ?? null;

        if (field.options && field.options.length > 0) {
          // Reuse an option's saved id when its text is unchanged.
          const prevOptsByText = new Map<string, any>();
          for (const o of ((prevField?.Options as any[]) || [])) {
            const t = typeof o === 'string' ? o : (o?.OptionText ?? o?.OptionValue ?? '');
            prevOptsByText.set(norm(t), o);
          }
          fq.Options = field.options.map(opt => {
            const prevOpt = prevOptsByText.get(norm(opt));
            if (prevOpt && typeof prevOpt !== 'string') return prevOpt;
            return this.makeOption(opt);
          });
        }
        if (!fq.Options || fq.Options.length === 0) {
          if (fq.Type === 'RadioButton' || fq.Type === 'DropDownListSelection') {
            fq.Options = ['Yes', 'No'].map(o => this.makeOption(o));
          } else if (fq.Type === 'CheckBox') {
            fq.Options = ['Yes'].map(o => this.makeOption(o));
          }
        }

        fq.QuestionHtml = this.generateQuestionHtml(fq);
        fq.AnswerHtml = this.generateAnswerHtml(fq);
        return fq;
      });

      return sectionInfo;
    });

    const deletedSectionIds: string[] = [];
    for (const s of (existing || [])) {
      if ((s as any).UniqueID && !usedSectionIds.has((s as any).UniqueID)) {
        deletedSectionIds.push((s as any).UniqueID);
      }
    }

    // Keep the hierarchy numbers on labels in sync with the edited form's rules
    // (a moved/added/removed branch re-numbers correctly; idempotent).
    this.applyHierarchicalLabelNumbers(built, edited.conditionalRules);

    return { sections: built, deletedSectionIds, newFieldIds };
  }

  // ============================================================
  // PUBLIC: Persist an AI-edited live form. Deletes removed sections, upserts the
  // rest (SaveSection updates in place because GUIDs were preserved), refreshes
  // localStorage, AUTO-MAPS any brand-new questions in the Attribute Mapper
  // (existing mappings untouched), and — only when asked — applies conditional
  // logic.
  // ============================================================
  //   applyConditionalLogic:
  //     • live AI edit  → true  (apply only when the instruction produced rules)
  //     • delta re-import → false (leave the saved conditional logic alone)
  saveEditedForm(
    merged: { sections: SectionInfo[]; deletedSectionIds: string[]; newFieldIds: string[] },
    existing: SectionInfo[],
    editedForm: ParseResult,
    templateId: string,
    applyConditionalLogic: boolean
  ): Observable<EditSaveResult> {
    const result: EditSaveResult = {
      success: true, sectionsSaved: 0, sectionsDeleted: 0, errors: []
    };

    const existingById = new Map<string, any>();
    for (const s of (existing || [])) existingById.set((s as any).UniqueID, s);

    // 1) Delete sections that no longer exist (pass the full saved object).
    const deletes = (merged.deletedSectionIds || [])
      .map(id => existingById.get(id))
      .filter(Boolean);

    const delete$ = deletes.length
      ? from(deletes).pipe(
          concatMap((sec: any) => this.apiService.DeleteSection(sec).pipe(
            tap(() => result.sectionsDeleted++),
            catchError(err => {
              result.errors.push(`Failed to delete section "${sec?.Title}": ${err.message}`);
              return of(null);
            })
          )),
          last(),
          map(() => null)
        )
      : of(null);

    return delete$.pipe(
      // 2) Upsert each section sequentially.
      switchMap(() => merged.sections.length
        ? from(merged.sections).pipe(
            concatMap(sec => this.apiService.SaveSection(sec).pipe(
              tap(() => result.sectionsSaved++),
              catchError(err => {
                result.errors.push(`Failed to save section "${sec.Title}": ${err.message}`);
                return of(null);
              })
            )),
            last(),
            map(() => null)
          )
        : of(null)
      ),
      // 3) Refresh local cache so the page reflects the new structure.
      tap(() => localStorage.setItem('AllQuestions', JSON.stringify([{ Section: merged.sections }]))),
      // 4) Auto-map ONLY the new questions (existing attribute names untouched).
      switchMap(() => this.saveNewAttributeMappings(
        merged.sections, editedForm.conditionalRules, templateId
      ).pipe(catchError(() => of(null)))),
      // 5) Conditional logic — only on live AI edits, and only if rules exist.
      switchMap(() => (applyConditionalLogic && editedForm.conditionalRules?.length)
        ? this.applyConditionalLogicForEdit(merged.sections, editedForm.conditionalRules, templateId)
            .pipe(catchError(() => of(null)))
        : of(null)),
      map(() => {
        result.success = result.errors.length === 0;
        return result;
      })
    );
  }

  // ============================================================
  // PRIVATE: Sync the Attribute Mapper — map new questions + self-heal broken names.
  // ============================================================
  // Builds hierarchical names for the whole form, then writes:
  //   • NEW fields (no saved mapping)            → fresh mapping with a new id.
  //   • EXISTING fields whose saved name is the   → corrected IN PLACE (reusing the
  //     broken doubled pattern "Q1_Q1"/"Q6.1_Q6_1"  saved mapping id so it updates,
  //     left by the earlier bug)                    not duplicates).
  // Clean auto names and any names the user edited by hand are left untouched.
  private saveNewAttributeMappings(
    sections: SectionInfo[],
    rules: ParsedConditionalRule[],
    templateId: string
  ): Observable<any> {
    let formId = 0;
    try { formId = Number(JSON.parse(localStorage.getItem('FormId') ?? '0')) || 0; } catch { formId = 0; }

    const all = this.buildAttributeMappings(sections, templateId, rules || []);
    // "Q1_Q1", "Q6.1_Q6_1", "Q9.1_Q9_1_…" — a doubled Q-number prefix, the
    // signature of the earlier bug. Safe to auto-correct; clean/user names won't match.
    const isBrokenDoubled = (name: string) => /^Q[\d.]+_Q\d/i.test(name || '');

    return this.apiService.GetFormDataMappingByFormId(formId).pipe(
      catchError(() => of(null)),
      switchMap((resp: any) => {
        const data: any[] = resp?.data || resp?.Data || [];
        const existingByField = new Map<string, { name: string; uid: string }>();
        for (const m of data) {
          const av = (m.attributeValue || m.AttributeValue || '').toLowerCase();
          if (av) existingByField.set(av, {
            name: m.attributeName || m.AttributeName || '',
            uid: m.uniqueId || m.UniqueId || '',
          });
        }

        // Names we are NOT touching (clean existing / user-edited) — reserve them
        // so corrections/new entries never collide with them.
        const keptNames = new Set<string>();
        for (const [, info] of existingByField) {
          if (!isBrokenDoubled(info.name)) keptNames.add((info.name || '').toLowerCase());
        }

        const pending: { AttributeValue: string; AttributeName: string; UniqueId: string }[] = [];
        for (const a of (all.Attributes || [])) {
          const av = (a.AttributeValue || '').toLowerCase();
          const existing = existingByField.get(av);
          if (!existing) {
            pending.push(a);                                   // new field → fresh id
          } else if (isBrokenDoubled(existing.name)) {
            pending.push({ ...a, UniqueId: existing.uid });    // fix in place (reuse id)
          }
          // else: clean or hand-edited name → leave it alone.
        }
        if (!pending.length) return of(null);

        // Dedupe the names we're about to write.
        const usedNames = new Set<string>(keptNames);
        const finalEntries = pending.map(e => {
          let name = e.AttributeName;
          let n = 2;
          while (usedNames.has(name.toLowerCase())) { name = `${e.AttributeName}_${n++}`; }
          usedNames.add(name.toLowerCase());
          return { ...e, AttributeName: name };
        });

        return this.apiService.SaveAttributeDataMap({
          TemplateID: templateId, FormID: formId, Attributes: finalEntries
        }).pipe(catchError(() => of(null)));
      })
    );
  }

  // ============================================================
  // PRIVATE: Apply conditional logic from edit-derived rules (replace-all).
  // ============================================================
  private applyConditionalLogicForEdit(
    sections: SectionInfo[],
    rules: ParsedConditionalRule[],
    templateId: string
  ): Observable<any> {
    const hiddenNames = new Set<string>();
    for (const r of (rules || [])) {
      if (r.targetFieldName) hiddenNames.add(r.targetFieldName);
      for (const d of (r.mandatoryDeps || [])) hiddenNames.add(d);
    }
    const logic = this.generateConditionalLogic(templateId, sections, rules, hiddenNames);
    return this.apiService.SaveConditionalLogic(logic).pipe(
      tap(() => { try { localStorage.setItem('ConditionalLogicData', JSON.stringify(logic)); } catch { /* ignore */ } }),
    );
  }

  // ============================================================
  // PRIVATE: Read Excel file
  // ============================================================

  private async readExcelFile(file: File): Promise<XLSX.WorkBook> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        try {
          const data = new Uint8Array(e.target.result);
          // cellStyles:true so worksheet['!rows'][i].hidden is populated — the
          // macro's row-hidden state is our initial-visibility source of truth.
          const workbook = XLSX.read(data, { type: 'array', cellStyles: true });
          resolve(workbook);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsArrayBuffer(file);
    });
  }

  // ============================================================
  // PRIVATE: Parse workbook — detect format and extract data
  // ============================================================

  private parseWorkbook(workbook: XLSX.WorkBook): ParseResult {
    const result: ParseResult = {
      success: true, sections: [], totalFields: 0,
      errors: [], warnings: [], conditionalRules: []
    };

    // Workbooks may have many tabs (Introduction, Submitting Organization,
    // Questionnaire, Results, Version Control). Pick the LARGEST sheet whose
    // first row looks like structured form headers — that's the actual question
    // sheet. Falls back to first sheet if nothing structured found.
    const isStructuredHeaders = (hs: string[]) =>
      hs.some(h =>
        h === 'label' || h === 'question' || h === 'type' ||
        h.includes('scoping question') || h.includes('question text') ||
        h.includes('response option') || h.includes('field label') ||
        h.includes('field name'));

    // Pick the largest structured sheet. Track its worksheet too so we can read
    // the per-row hidden flags (worksheet['!rows'][i].hidden) — the macro hides
    // dependent question rows until answered, and that is the authoritative
    // initial-visibility signal we propagate to the parsed fields.
    let chosenData: any[][] = XLSX.utils.sheet_to_json(workbook.Sheets[workbook.SheetNames[0]], { header: 1 });
    let chosenSheet: XLSX.WorkSheet = workbook.Sheets[workbook.SheetNames[0]];
    let bestStructuredRows = 0;
    for (const name of workbook.SheetNames) {
      const sheetData = XLSX.utils.sheet_to_json(workbook.Sheets[name], { header: 1 }) as any[][];
      if (!sheetData?.length) continue;
      const hs = sheetData[0]?.map((h: any) => String(h || '').toLowerCase().trim()) || [];
      if (isStructuredHeaders(hs) && sheetData.length > bestStructuredRows) {
        chosenData = sheetData;
        chosenSheet = workbook.Sheets[name];
        bestStructuredRows = sheetData.length;
      }
    }
    const data = chosenData;

    if (!data || data.length === 0) {
      result.success = false;
      result.errors.push('Excel file is empty');
      return result;
    }

    // Row-hidden flags aligned to `data` row indices (true = hidden in source).
    const sheetRows: any[] = (chosenSheet && (chosenSheet as any)['!rows']) || [];
    const hiddenRows: boolean[] = data.map((_row, i) => !!(sheetRows[i] && sheetRows[i].hidden));

    const headers = data[0]?.map((h: any) => String(h || '').toLowerCase().trim()) || [];
    const isStructured = isStructuredHeaders(headers);

    if (isStructured) {
      this.parseStructuredFormat(data, result, hiddenRows);
    } else {
      this.parseFreeFormFormat(data, result);
    }

    // Auto-detect conditional rules from the field structure
    this.detectConditionalRules(result);

    return result;
  }

  // ============================================================
  // PRIVATE: Parse structured Excel (with column headers)
  // ============================================================

  private parseStructuredFormat(data: any[][], result: ParseResult, hiddenRows: boolean[] = []): void {
    const headers = data[0].map((h: any) => String(h || '').toLowerCase().trim());

    // findColumn now does PARTIAL matches so headers like "scoping question",
    // "field label", "response options" still resolve.
    const cols = {
      label:     this.findColumnPartial(headers, ['scoping question', 'question text', 'question', 'label', 'field label', 'field']),
      type:      this.findColumnPartial(headers, ['response options', 'response type', 'response', 'type', 'field type', 'fieldtype']),
      required:  this.findColumn(headers, ['required', 'mandatory', 'req']),
      options:   this.findColumn(headers, ['options', 'choices', 'values']),
      section:   this.findColumnPartial(headers, ['section', 'group', 'category', 'identifier']),
      helpText:  this.findColumnPartial(headers, ['guidance', 'help text', 'helptext', 'help', 'tooltip', 'notes']),
      name:      this.findColumnPartial(headers, ['number', 'question id', 'question identifier', 'name', 'fieldname', 'field name', 'id', 'control']),
      condition: this.findColumnPartial(headers, ['condition', 'conditional', 'show when', 'show if', 'visible when', 'depends']),
    };

    if (cols.label === -1) {
      result.success = false;
      result.errors.push('Could not find a question / label column in Excel');
      return;
    }

    // ── CCRA-style detection ──────────────────────────────────────────────
    // Some workbooks (e.g. DIB SCC Cybersecurity Compliance & Risk Assessment)
    // pack TWO logical tables into one sheet:
    //   1) Scoping questions (Number / Question Identifier / Scoping Question / Response Options)
    //   2) A controls sub-table that re-uses the Number column for control IDs
    //      ("3.1.1", "3.1.4", …) and the Identifier column for "Category" numbers.
    //
    // We locate the boundary STRUCTURALLY first, falling back to a literal header
    // match:
    //   • Structural — the first row whose Number/Control column is a multi-segment
    //     numeric ID like "3.1.1" (≥2 dots, all numeric). Scoping numbers are only
    //     "1" / "1.a" / "12.a" so they never collide with this. If the row right
    //     above it is a non-numeric "header-ish" row (e.g. "Control / Category / …"),
    //     that row is recorded so it can be skipped.
    //   • Literal — a row whose cells read like ["Control","Category","… control …"].
    //
    // When this pattern is detected we:
    //   • use ONE section (don't fragment by the Identifier column),
    //   • name fields as `{cleanedIdentifier}{normalizedNumber}` for scoping rows
    //     (FCI1, CUI2a, Risk9a) and as the bare number for controls (3.1.1),
    //   • tag fields with metadata so detectConditionalRules can build
    //     gateway-aware Yes/No branches and transitive mandatory-dependent sets.
    // A control ID is a multi-segment numeric ID, with an OPTIONAL short letter
    // prefix: "3.1.1" / "3.10.1" (CCRA) and "C.1.1" / "C.3.4" (Job Application
    // form). A bare top-level number ("1") or a sub-letter id ("1.a") never
    // matches — it needs ≥2 numeric segments after the optional prefix.
    const isMultiSegmentId = (v: any): boolean =>
      /^[A-Za-z]{0,3}[.\-]?\d+[.\-]\d+(?:[.\-]\d+)*$/.test(String(v ?? '').trim());

    let controlSubTableStartRow = -1;   // index of the FIRST control data row
    let controlHeaderRow = -1;          // index of a header row to skip (or -1)
    for (let i = 1; i < data.length; i++) {
      const row = data[i] || [];
      const c0 = String(row[0] || '').toLowerCase().trim();
      const c1 = String(row[1] || '').toLowerCase().trim();
      const c2 = String(row[2] || '').toLowerCase().trim();

      // (a) Literal header row whose first cell is "Control" and a neighbouring
      //     cell reads like a controls-table header — "Category" (CCRA),
      //     "Tier"/"Status"/"Response" (Job form), or "Control".
      if (c0 === 'control' && /\b(category|tier|response|status|control)\b/.test(`${c1} ${c2}`)) {
        controlHeaderRow = i;
        controlSubTableStartRow = i + 1;
        break;
      }

      // (b) Structural — first multi-segment control ID in the Number column.
      if (cols.name !== -1 && isMultiSegmentId(row[cols.name])) {
        controlSubTableStartRow = i;
        const prev = data[i - 1] || [];
        const prev0 = String(prev[0] ?? '').trim();
        const prevIsHeaderish =
          prev0 !== '' && !/^\d/.test(prev0) &&
          !(cols.name !== -1 && isMultiSegmentId(prev[cols.name]));
        if (prevIsHeaderish) controlHeaderRow = i - 1;
        break;
      }
    }
    const isCcraStyle = controlSubTableStartRow > 0;

    const sectionsMap = new Map<string, ParsedSection>();
    const ccraSectionKey = '__CCRA_FORM__';
    if (isCcraStyle) {
      sectionsMap.set(ccraSectionKey, { title: 'Form Questions', fields: [] });
    }

    // Identifier prefixes ("Rlsk" is a known typo in the CCRA template; collapse
    // "CUI;FCI" to its first segment so the resulting names stay short).
    const cleanIdentifier = (raw: string): string => {
      let s = (raw || '').trim();
      if (!s) return '';
      s = s.split(/[;,/|]/)[0].trim();
      if (/^rlsk$/i.test(s)) s = 'Risk';
      return s;
    };

    // "1" → "1", "1.a" → "1a", "3.1.1" → "3.1.1"
    const normaliseNumber = (raw: string): string =>
      raw.replace(/^(\d+(?:\.\d+)*)\.([a-z])$/i, '$1$2');

    for (let i = 1; i < data.length; i++) {
      const row = data[i];
      if (!row || !row[cols.label]) continue;

      const label = String(row[cols.label]).trim();
      if (!label) continue;

      const identifierRaw = cols.section !== -1 && row[cols.section]
        ? String(row[cols.section]).trim()
        : '';
      const rawNumberForSection = cols.name !== -1 && row[cols.name]
        ? String(row[cols.name]).trim()
        : '';

      // Skip the header row that delimits the control sub-table — it's not a
      // question, it's metadata. (Located above; -1 when there is none.)
      if (isCcraStyle && i === controlHeaderRow) continue;

      // Skip banner / separator rows that carry ONLY a free-text label with no
      // number, no identifier and no response type — e.g. a "SCOPING QUESTIONS"
      // section title. These would otherwise become a junk question (named Q1)
      // that shows on load. (CCRA-style sheets only, where rows are identifier/
      // number-tagged; legacy free-form sheets legitimately have label-only rows.)
      if (isCcraStyle && !rawNumberForSection && !identifierRaw
          && (cols.type === -1 || !row[cols.type])) {
        continue;
      }

      let sectionName: string;
      if (isCcraStyle) {
        sectionName = ccraSectionKey;
      } else {
        // Legacy behaviour: group by Identifier column with `{Identifier}{firstQ#}` title.
        const groupKey = identifierRaw || 'Form Questions';
        if (!sectionsMap.has(groupKey)) {
          const baseNum = rawNumberForSection.match(/^\d+/)?.[0] || '';
          const title = identifierRaw
            ? (baseNum ? `${identifierRaw}${baseNum}` : identifierRaw)
            : (baseNum || 'Form Questions');
          sectionsMap.set(groupKey, { title, fields: [] });
        }
        sectionName = groupKey;
      }

      const rawType = cols.type !== -1 ? String(row[cols.type] || 'text').toLowerCase().trim() : 'text';
      const rawNameValue = cols.name !== -1 && row[cols.name] ? String(row[cols.name]).trim() : '';

      // CCRA-style: identifier-prefixed labels for scoping rows, bare numbers for controls
      const inControlSubTable = isCcraStyle && i >= controlSubTableStartRow;
      const numberPart = inControlSubTable ? rawNameValue : normaliseNumber(rawNameValue);
      const cleanedIdentifier = inControlSubTable ? '' : cleanIdentifier(identifierRaw);

      const normaliseName = (raw: string) => {
        if (isCcraStyle) {
          if (inControlSubTable) {
            // Controls keep their bare number: "3.1.1", "3.10.1", …
            return numberPart || `C${result.totalFields + 1}`;
          }
          // Scoping rows: "{Identifier}{Number}" → "FCI1", "FCI1a", "CUI2a", "Risk9a"
          if (cleanedIdentifier && numberPart) return `${cleanedIdentifier}${numberPart}`;
          if (numberPart) return numberPart;
          return `Q${result.totalFields + 1}`;
        }
        // Legacy: "Q"-prefix names
        if (!raw) return `Q${result.totalFields + 1}`;
        if (/^[Qq]/.test(raw)) return raw;
        if (/^\d/.test(raw)) {
          return 'Q' + raw.replace(/^(\d+(?:\.\d+)*)\.([a-z])$/i, '$1$2');
        }
        return raw;
      };

      // Default to required=true when no explicit required column is present but
      // the row HAS a Response Options value (it's a real input question, not a
      // descriptive header). Structured forms expect every question to be answered.
      const hasExplicitRequired = cols.required !== -1;
      const hasResponseOptions = cols.type !== -1 && !!row[cols.type];
      const fieldRequired = hasExplicitRequired
        ? this.parseRequired(row[cols.required])
        : (hasResponseOptions || /\*/.test(label));

      const field: ParsedField = {
        name: normaliseName(rawNameValue),
        label,
        type: rawType,
        required: fieldRequired,
        helpText: cols.helpText !== -1 ? String(row[cols.helpText] || '') : ''
      };

      // Stash CCRA-style metadata for downstream conditional-logic generation.
      if (isCcraStyle) {
        field.identifierGroup = inControlSubTable ? '' : cleanedIdentifier;
        field.isControl = inControlSubTable;
        field.numberPart = numberPart;
      }

      // Map common "Response Options" cell values to our internal types
      const lowerType = rawType.toLowerCase();
      if (/select\s*option|select\s*one|drop\s*down|dropdown/.test(lowerType)) {
        field.type = 'dropdown';
        if (!field.options) field.options = ['Yes', 'No'];
      } else if (/yes\s*\/?\s*no|y\/n/i.test(lowerType)) {
        field.type = 'radio';
        field.options = ['Yes', 'No'];
      } else if (/true\s*\/?\s*false/i.test(lowerType)) {
        field.type = 'radio';
        field.options = ['True', 'False'];
      } else if (/dd\s*\/\s*mm\s*\/\s*yy|mm\s*\/\s*dd\s*\/\s*yy|yyyy.*mm.*dd|date/i.test(lowerType)) {
        field.type = 'date';
      } else if (/score|number|numeric|integer|amount/i.test(lowerType)) {
        field.type = 'textbox';
        field.expression = 6;
      } else if (/text\s*area|long\s*text|multi.?line|enter\s*text/i.test(lowerType)) {
        field.type = 'textarea';
      } else if (rawType === 'no' || rawType === 'yes') {
        // CRA-style cell that is literally "No" or "Yes" → default to a Yes/No radio
        field.type = 'radio';
        field.options = ['Yes', 'No'];
      } else if (!field.type || field.type === 'text') {
        field.type = 'textbox';
      }

      if (cols.options !== -1 && row[cols.options]) {
        field.options = String(row[cols.options])
          .split(/[,;|]/)
          .map(o => o.trim())
          .filter(o => o);
      }

      // Auto-detect expression from label
      field.expression = this.detectExpression(label);

      // Carry the source row's hidden state — the macro hides dependent rows
      // until answered; this is the authoritative initial-visibility signal.
      field.initiallyHidden = !!hiddenRows[i];

      // "Mandatory" drives initial visibility in the preview (mandatory nodes
      // show on load, the rest are revealed by their parent). So a question is
      // mandatory IFF its source row is VISIBLE; a hidden row is non-mandatory
      // and stays collapsed until revealed. Only override when the workbook
      // actually carries hidden-row info (otherwise keep the parsed default and
      // show everything — the safe fallback).
      if (hiddenRows.some(Boolean)) {
        field.required = !field.initiallyHidden;
      }

      sectionsMap.get(sectionName)!.fields.push(field);
      result.totalFields++;

      // Parse explicit condition column
      // Supported formats:
      //   "if FieldName = Value"          → show this field when source=Value
      //   "if FieldName = Value then show" → same
      //   "FieldName = Value"             → same (shorthand)
      //   "FieldName != Value"            → show when NOT that value (skip when value matches)
      if (cols.condition !== -1 && row[cols.condition]) {
        const rule = this.parseConditionExpression(
          String(row[cols.condition]).trim(),
          field.name
        );
        if (rule) {
          result.conditionalRules.push(rule);
        } else {
          result.warnings.push(`Row ${i + 1}: Could not parse condition "${row[cols.condition]}"`);
        }
      }
    }

    result.sections = Array.from(sectionsMap.values());
    result.success = result.sections.length > 0;
    if (!result.success) {
      result.errors.push('No valid fields found in Excel');
    }
  }

  // ============================================================
  // PRIVATE: Parse condition expression from the Condition column
  // ============================================================

  /**
   * Parses condition expressions like:
   *   "if Q5 = Yes"
   *   "if Q5 = Yes then show"
   *   "Q5 = Yes"
   *   "Q5 != Yes"
   *
   * Returns a ParsedConditionalRule where:
   *   - sourceFieldName = the field being checked (Q5)
   *   - triggerValue = the value that triggers showing this field (Yes)
   *   - targetFieldName = the current field (the one this condition is on)
   *   - skipToFieldName = undefined (resolved later during detectConditionalRules)
   */
  private parseConditionExpression(expr: string, currentFieldName: string): ParsedConditionalRule | null {
    // Strip leading "if " (case-insensitive)
    let cleaned = expr.replace(/^if\s+/i, '').trim();

    // Strip trailing "then show" / "then skip" (optional)
    cleaned = cleaned.replace(/\s+then\s+(show|skip)\s*$/i, '').trim();

    // Match: FieldName = Value  OR  FieldName != Value
    const neqMatch = cleaned.match(/^(.+?)\s*!=\s*(.+)$/);
    const eqMatch = !neqMatch ? cleaned.match(/^(.+?)\s*=\s*(.+)$/) : null;

    if (eqMatch) {
      return {
        sourceFieldName: eqMatch[1].trim(),
        triggerValue: eqMatch[2].trim(),
        targetFieldName: currentFieldName
      };
    }

    if (neqMatch) {
      // "!=" means: the field is visible when the value does NOT match.
      // We invert this: the source should skip to the NEXT field when it DOES match.
      // For now, store as a normal rule with the opposite meaning —
      // generateConditionalLogic handles the skip direction.
      return {
        sourceFieldName: neqMatch[1].trim(),
        triggerValue: neqMatch[2].trim(),
        targetFieldName: currentFieldName,
        skipToFieldName: '__SKIP_THIS__'  // marker: skip this field when value matches
      };
    }

    return null;
  }

  // ============================================================
  // PRIVATE: Parse free-form Excel (like CM Form)
  // ============================================================

  private parseFreeFormFormat(data: any[][], result: ParseResult): void {
    let currentSection: ParsedSection | null = null;
    let lastMainNum = '';

    // Next available sub-letter for the current parent question (a, b, c, ...)
    const nextSubLetter = (sec: ParsedSection, num: string): string => {
      const used = sec.fields.filter(f => new RegExp(`^Q${num}[a-z]$`).test(f.name)).length;
      return String.fromCharCode('a'.charCodeAt(0) + used);
    };

    for (let i = 0; i < data.length; i++) {
      const row = data[i];
      const firstCol  = String(row?.[0] || '').trim();
      const secondCol = String(row?.[1] || '').trim();

      if (!firstCol && !secondCol) continue;

      // Section headers
      if (this.isSectionHeader(firstCol)) {
        if (currentSection && currentSection.fields.length > 0) {
          result.sections.push(currentSection);
        }
        currentSection = { title: firstCol, fields: [] };
        continue;
      }

      // Numbered questions: "15. ...", "52.12.3-2 ...", "3.1) ..."
      const questionMatch = firstCol.match(/^([\d]+(?:[.\-][\d]+)*)\s*[\.\)]\s*(.+)/);
      if (questionMatch) {
        if (!currentSection) currentSection = { title: 'Form Questions', fields: [] };
        lastMainNum = questionMatch[1];
        const { type, label, required, options, expression } = this.parseQuestionText(questionMatch[2]);
        currentSection.fields.push({
          name: `Q${lastMainNum}`,
          label,
          type,
          required,
          options: options.length > 0 ? options : undefined,
          expression
        });
        result.totalFields++;
        continue;
      }

      // Metals (CM Form pattern): give each metal its own letter under the parent question
      const metalMatch = (firstCol || secondCol).match(
        /^(Tantalum|Tin|Gold|Tungsten)\s*\(?\*?\)?\s*(?:[-–]?\s*Radio\s*Button\s*\(?\s*True\s*\/?\s*False\s*\)?)?/i
      );
      if (metalMatch && currentSection && lastMainNum) {
        const metal = metalMatch[1];
        const isRequired = (firstCol + secondCol).includes('*');
        const letter = nextSubLetter(currentSection, lastMainNum);
        const baseName = `Q${lastMainNum}${letter}`;

        currentSection.fields.push({
          name: baseName,
          label: metal,
          type: 'radio',
          required: isRequired,
          options: ['True', 'False']
        });
        result.totalFields++;

        // Comments textarea — shown when the metal radio is True (handled by detectConditionalRules)
        currentSection.fields.push({
          name: `${baseName}_Comments`,
          label: `${metal} - Comments`,
          type: 'textarea',
          required: false
        });
        result.totalFields++;
        continue;
      }

      // "Please indicate ..." → its own sub-question (textarea)
      const indicateMatch = firstCol.match(/please indicate/i) || secondCol.match(/please indicate/i);
      if (indicateMatch && currentSection && lastMainNum) {
        const rawText = (firstCol || secondCol).replace(/^\d+\.\s*/, '').trim();
        const parsed = this.parseQuestionText(rawText);
        const letter = nextSubLetter(currentSection, lastMainNum);
        currentSection.fields.push({
          name: `Q${lastMainNum}${letter}`,
          label: parsed.label || 'Please indicate in which products or processes',
          // Default to textarea when no stronger type was detected — "please indicate"
          // questions almost always expect a free-text answer.
          type: parsed.type === 'textbox' ? 'textarea' : parsed.type,
          required: parsed.required,
          options: parsed.options.length > 0 ? parsed.options : undefined,
          expression: parsed.expression
        });
        result.totalFields++;
        continue;
      }

      // Generic sub-question: only fire for rows that carry an explicit type hint
      // ("- Text Entry", "- Radio Button", "- Dropdown Selection", etc.).
      // Free-form Excel often has annotation rows in column B like
      //   "conditional logic - if yes need to fill out comments, if no skip"
      // — those are notes, not fields, so we don't want to invent a Q for them.
      if (currentSection && lastMainNum) {
        const text = firstCol || secondCol;
        const hasTypeHint = /[-–]\s*(Text\s*Entry|Text\s*Input|Long\s*Text|Multi.?line|Text\s*Area|Radio\s*Button|Dropdown\s*Selection|Drop\s*Down|Date\s*Entry|Numeric\s*Entry|File\s*Upload|Attachment|Check\s*Box)\b/i.test(text);

        if (hasTypeHint && text.length <= 250 && text.length >= 3) {
          const parsed = this.parseQuestionText(text);
          const letter = nextSubLetter(currentSection, lastMainNum);
          currentSection.fields.push({
            name: `Q${lastMainNum}${letter}`,
            label: parsed.label,
            type: parsed.type,
            required: parsed.required,
            options: parsed.options.length > 0 ? parsed.options : undefined,
            expression: parsed.expression
          });
          result.totalFields++;
          continue;
        }
      }
    }

    if (currentSection && currentSection.fields.length > 0) {
      result.sections.push(currentSection);
    }

    result.success = result.sections.length > 0 && result.totalFields > 0;
    if (!result.success) {
      result.errors.push('Could not detect any sections or questions in the Excel file');
      result.warnings.push('Ensure questions start with numbers (e.g., "1. Company Name")');
    }
  }

  // ============================================================
  // PRIVATE: Parse text with style metadata (Word / PDF primary parser)
  // ------------------------------------------------------------
  // Handles a wide range of numbering conventions seen in real forms:
  //   "1. Company Name"         → Q1
  //   "1. (a) Corporate ID"     → Q1a
  //   "(b) Global location"     → Q{lastNum}b   (continuation of previous numbered question)
  //   "(c) (i) Address Line I"  → Q{lastNum}ci
  //   "(i) Regulated"           → Q{lastNum}{lastLetter}i   (sub-sub of last sub-letter)
  //   "a. Some sub"             → Q{lastNum}a   (sub-letter without parens)
  //
  // Form widget data (when present on the TextLine — PDF only) takes priority over
  // text-based type guesses, so dropdowns / radios / dates are detected correctly.
  // ============================================================

  private parseTextWithStyles(lines: TextLine[], result: ParseResult): void {
    let currentSection: ParsedSection | null = null;
    let lastMainNum = '';      // e.g., "1", "2", "12.3"
    let lastSubLetter = '';    // e.g., "a", "b"
    let lastMarkerKind: 'letter' | 'roman' | null = null;
    let lastField: ParsedField | null = null;
    let questionNum = 0;

    // Decide whether a "(x)" marker is acting as a sub-letter or a sub-sub-roman.
    // Single letters in [ivxlcdm] are valid in BOTH roles (e.g. (c) might be the
    // 3rd alphabetical sub-marker, OR a Roman 100). Resolve by context:
    //   - multi-char marker: roman iff all chars are roman digits
    //   - single-char marker:
    //       * == lastSubLetter+1 (alphabetical next)  → letter (the most common case)
    //       * previous marker was roman + char is roman → roman (continuing roman sequence)
    //       * previous marker was a letter & char > lastSubLetter → letter (skipped letter)
    //       * char is roman + lastSubLetter exists → roman (starting roman sequence)
    //       * otherwise → letter
    const classifyMarker = (marker: string): 'letter' | 'roman' => {
      if (marker.length > 1) {
        return /^[ivxlcdm]+$/i.test(marker) ? 'roman' : 'letter';
      }
      const ch = marker.toLowerCase();
      const isRomanChar = /^[ivxlcdm]$/.test(ch);
      if (!lastSubLetter) return 'letter';
      const expected = String.fromCharCode(lastSubLetter.charCodeAt(0) + 1);
      if (ch === expected) return 'letter';
      if (lastMarkerKind === 'roman' && isRomanChar) return 'roman';
      // Large alphabetical jump to a Roman digit is a level transition (e.g. a → i),
      // not a letter skip. Without this, (i) after (a) becomes Q1i instead of Q1ai.
      if (isRomanChar && (ch.charCodeAt(0) - lastSubLetter.charCodeAt(0)) > 2) return 'roman';
      if (lastMarkerKind === 'letter' && ch > lastSubLetter) return 'letter';
      if (isRomanChar) return 'roman';
      return 'letter';
    };

    // Inline options observed on a separate line ("Yes No", "True False") may apply to
    // EITHER the previous field or the next one — depending on which side of the radio
    // widget the text was extracted from. When the next line is a question marker, the
    // options belong to that next question, not the current `lastField`.
    let pendingOptions: string[] | null = null;

    // Helper: apply any pending options to a freshly-created field
    const applyPending = (f: ParsedField) => {
      if (pendingOptions && (!f.options || f.options.length === 0)) {
        f.type = 'radio';
        f.options = pendingOptions;
      }
      pendingOptions = null;
      return f;
    };

    const ensureSection = () => {
      if (!currentSection) currentSection = { title: 'Form Questions', fields: [] };
      return currentSection;
    };

    // Returns options if the line is a pure inline-options line ("Yes No", "True False")
    const detectInlineOptionsLine = (t: string): string[] | null => {
      const stripped = t.replace(/[○●•◦⦿⚫()\[\]]/g, '').trim();
      if (/^Yes\s+No$/i.test(stripped) || /^No\s+Yes$/i.test(stripped)) return ['Yes', 'No'];
      if (/^True\s+False$/i.test(stripped) || /^False\s+True$/i.test(stripped)) return ['True', 'False'];
      return null;
    };

    // Detect "From (DD/MM/YYYY) ... To (DD/MM/YYYY)" lines so the previous question
    // (which is really a date range) can be split into two date fields.
    const isFromToDateLine = (t: string): boolean => {
      const lower = t.toLowerCase();
      const hasFrom = /\bfrom\b/.test(lower);
      const hasTo = /\bto\b/.test(lower);
      const ddMmCount = (t.match(/DD\s*\/\s*MM\s*\/\s*YY+/gi) || []).length;
      return hasFrom && hasTo && ddMmCount >= 1;
    };

    // Split a line that holds multiple labelled fields into separate pseudo-questions.
    // Catches:
    //   "Firm Name:  Email:  Phone:"                       (colon labels)
    //   "Last Name*:  First Name*:"                        (colon labels with required *)
    //   "Gender*:  [checkboxes]  Date of Birth**"          (mixed colon + ** suffix)
    //   "City    State    Zip    Email"                    (tab/3-space-separated headers)
    const splitMultiFieldLine = (t: string): string[] | null => {
      type Hit = { pos: number; label: string };
      const hits: Hit[] = [];

      // (i) Colon-/question-mark-/hash-terminated labels — allow * inside the name
      //     e.g. "Last Name*:", "Firm Name:", "Email - Contact:", "Phone#", "How many?"
      const colonRe = /([A-Za-z][A-Za-z0-9*&/()' \-]{0,40}?)\s*[:?#](?=\s|$|[^\w])/g;
      for (const m of t.matchAll(colonRe)) {
        hits.push({ pos: m.index ?? 0, label: m[1].trim() });
      }

      // (ii) "**" suffix labels — e.g. "Date of Birth**" (required, no colon)
      //     Require SINGLE-space separation between words so we don't gobble up
      //     intervening checkbox text. "Female    Date of Birth**" → matches only
      //     "Date of Birth", not "Female ... Birth".
      const starSuffixRe = /([A-Z][A-Za-z]+(?: [A-Za-z]+){0,4})\*{2,}(?=\s|$|[^\w])/g;
      for (const m of t.matchAll(starSuffixRe)) {
        hits.push({ pos: m.index ?? 0, label: m[1].trim() + '**' });
      }

      if (hits.length >= 2) {
        // de-duplicate hits that overlap at the same position (colon vs ** can match same word)
        hits.sort((a, b) => a.pos - b.pos);
        const uniq: Hit[] = [];
        for (const h of hits) {
          if (!uniq.length || h.pos - uniq[uniq.length - 1].pos > 3) uniq.push(h);
        }
        if (uniq.length >= 2) {
          return uniq.map(h => h.label).filter(s => s.length >= 2);
        }
      }

      // (iii) Tab-separated headers — last-resort fallback for "City  State  Zip"
      const tabParts = t.split(/\t+|\s{3,}/).map(s => s.trim()).filter(Boolean);
      if (tabParts.length >= 2 && tabParts.length <= 8) {
        const allShortLabels = tabParts.every(p =>
          p.length <= 50 && !/[.?!]$/.test(p) && p.split(/\s+/).length <= 6);
        if (allShortLabels) return tabParts.map(p => p.replace(/^\*\s*/, '').trim());
      }
      return null;
    };

    for (let lineIdx = 0; lineIdx < lines.length; lineIdx++) {
      const line = lines[lineIdx];
      // Strip Word/PDF "fillable" markers — runs of 3+ underscores or dots used
      // to represent answer space ("________ Phone# ________"). After cleanup,
      // a line containing only fill markers becomes empty and is skipped.
      let text = line.text
        .replace(/_{3,}/g, ' ')
        .replace(/\.{6,}/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      if (!text) continue;
      // Also reject lines that are pure punctuation/markers after cleanup
      if (!/[A-Za-z0-9]/.test(text)) continue;

      // (0a) "From (DD/MM/YYYY) To (DD/MM/YYYY)" → retroactively split lastField into
      //      two date fields ({name} = From, {name}_To = To)
      if (lastField && currentSection && isFromToDateLine(text)) {
        const original = lastField;
        const originalLabel = original.label;
        original.label = `${originalLabel} (From)`;
        original.type = 'date';
        original.options = undefined;

        const toField: ParsedField = {
          name: `${original.name}_To`,
          label: `${originalLabel} (To)`,
          type: 'date',
          required: original.required,
          expression: 0
        };
        currentSection.fields.push(toField);
        result.totalFields++;
        lastField = toField;
        continue;
      }

      // (0b) Multi-field line: "Firm Name:  Email:  Phone:" → 3 separate text fields.
      //      Also handles tab/space-separated headers like "City  State  Zip  Email".
      //      Only triggers when the line is NOT a numbered/paren/bullet question marker.
      if (currentSection && !/^\(?\s*\d+[\.\)]/.test(text) && !/^\(\s*[a-z]+\s*\)/i.test(text)
          && !/^[-•]\s/.test(text)) {
        const parts = splitMultiFieldLine(text);
        if (parts && parts.length >= 2) {
          for (const partLabel of parts) {
            const cleaned = partLabel.replace(/[:\s]+$/, '').trim();
            if (!cleaned) continue;
            const parsed = this.parseQuestionText(cleaned);
            let name: string;
            if (lastMainNum) {
              const used = currentSection.fields.filter(f =>
                new RegExp(`^Q${lastMainNum}[a-z]$`).test(f.name)).length;
              const letter = String.fromCharCode('a'.charCodeAt(0) + used);
              lastSubLetter = letter;
              name = `Q${lastMainNum}${letter}`;
            } else {
              questionNum++;
              lastMainNum = String(questionNum);
              lastSubLetter = '';
              name = `Q${lastMainNum}`;
            }
            const field = applyPending(this.buildParsedField(name, parsed, line));
            currentSection.fields.push(field);
            result.totalFields++;
            lastField = field;
          }
          continue;
        }
      }

      // (0) Pure inline-option line ("Yes No" / "True False"). Decide which question
      //     it belongs to:
      //       - if the NEXT line is a question marker, the options apply to that next
      //         question (PDFs sometimes extract option text before the question label
      //         due to multi-column layout)
      //       - if the line itself carries a widget, it's the radio anchor for the
      //         NEXT field
      //       - otherwise attach to lastField (the common in-reading-order case)
      if (!line.widgetType) {
        const opts = detectInlineOptionsLine(text);
        if (opts) {
          const next = lines[lineIdx + 1];
          const nextIsQuestion = next && /^\s*(?:\d+(?:[.\-]\d+)*\s*[\.\)]|\(\s*[a-z]+\s*\))/i.test(next.text.trim());
          if (nextIsQuestion || !lastField || lastField.options) {
            pendingOptions = opts;
          } else {
            lastField.type = 'radio';
            lastField.options = opts;
          }
          continue;
        }
      }

      // (1) Section header — bold/heading text that isn't a numbered/lettered question
      if (
        (line.isHeading || line.isBold) &&
        !/^\d+(?:[.\-]\d+)*\s*[\.\)]/.test(text) &&
        !/^\(\s*[a-z]\s*\)/i.test(text) &&
        !/^\(\s*[ivxlcdm]+\s*\)/i.test(text) &&
        text.length <= 100 &&
        !text.startsWith('(')
      ) {
        if (currentSection && currentSection.fields.length > 0) {
          result.sections.push(currentSection);
        }
        currentSection = { title: text, fields: [] };
        lastField = null;
        lastSubLetter = '';
        continue;
      }

      // (2) Compound numbering: "1. (a) ..." or "1. (a) (i) ..."
      let m = text.match(
        /^(\d+(?:[.\-]\d+)*)\.?\s*\(\s*([a-z])\s*\)\s*(?:\(\s*([ivxlcdm]+)\s*\)\s*)?(.+)/i
      );
      if (m) {
        const section = ensureSection();
        lastMainNum = m[1];
        lastSubLetter = m[2].toLowerCase();
        const roman = (m[3] || '').toLowerCase();
        lastMarkerKind = roman ? 'roman' : 'letter';
        const name = `Q${lastMainNum}${lastSubLetter}${roman}`;
        const field = applyPending(this.buildParsedField(name, this.parseQuestionText(m[4]), line));
        section.fields.push(field);
        result.totalFields++;
        lastField = field;
        continue;
      }

      // (3) Pure numbered: "1. ...", "12.3 ..."
      m = text.match(/^(\d+(?:[.\-]\d+)*)\s*[\.\)]\s*(.+)/);
      if (m) {
        const section = ensureSection();
        lastMainNum = m[1];
        lastSubLetter = '';
        lastMarkerKind = null;
        questionNum++;
        const field = applyPending(this.buildParsedField(`Q${lastMainNum}`, this.parseQuestionText(m[2]), line));
        section.fields.push(field);
        result.totalFields++;
        lastField = field;
        continue;
      }

      // (4) Paren marker: "(a) ...", "(b) (i) ...", "(i) ...", "(ii) ..."
      //     Disambiguates between sub-letter and sub-sub-roman based on context.
      m = text.match(/^\(\s*([a-z]+)\s*\)\s*(?:\(\s*([ivxlcdm]+)\s*\)\s*)?(.+)/i);
      if (m && lastMainNum) {
        const section = ensureSection();
        const outer = m[1].toLowerCase();
        const innerRoman = (m[2] || '').toLowerCase();
        const rest = m[3];

        const kind = classifyMarker(outer);
        let name: string;
        if (kind === 'letter') {
          lastSubLetter = outer;
          name = `Q${lastMainNum}${outer}${innerRoman}`;
          lastMarkerKind = innerRoman ? 'roman' : 'letter';
        } else {
          if (!lastSubLetter) {
            lastSubLetter = outer;
            name = `Q${lastMainNum}${outer}`;
            lastMarkerKind = 'letter';
          } else {
            name = `Q${lastMainNum}${lastSubLetter}${outer}`;
            lastMarkerKind = 'roman';
          }
        }

        const field = applyPending(this.buildParsedField(name, this.parseQuestionText(rest), line));
        section.fields.push(field);
        result.totalFields++;
        lastField = field;
        continue;
      }

      // (6) Metal sub-question (CM Form pattern): Tantalum/Tin/Gold/Tungsten
      const metalMatch = text.match(
        /^(Tantalum|Tin|Gold|Tungsten)\s*\(?\*?\)?\s*(?:[-–]?\s*Radio\s*Button\s*\(?\s*True\s*\/?\s*False\s*\)?)?/i
      );
      if (metalMatch && currentSection && lastMainNum) {
        const metal = metalMatch[1];
        // Use next-available letter under lastMainNum
        const existing = currentSection.fields.filter(f =>
          new RegExp(`^Q${lastMainNum}[a-z]$`).test(f.name)).length;
        const letter = String.fromCharCode('a'.charCodeAt(0) + existing);
        const baseName = `Q${lastMainNum}${letter}`;
        lastSubLetter = letter;

        const metalField: ParsedField = applyPending({
          name: baseName,
          label: metal,
          type: 'radio',
          required: text.includes('*'),
          options: ['True', 'False']
        });
        currentSection.fields.push(metalField);
        result.totalFields++;

        currentSection.fields.push({
          name: `${baseName}_Comments`,
          label: `${metal} - Comments`,
          type: 'textarea',
          required: false
        });
        result.totalFields++;
        lastField = metalField;
        continue;
      }

      // (7) Bare lettered sub-question: "a. ...", "a) ..."
      m = text.match(/^([a-z])\s*[\.\)]\s*(.+)/i);
      if (m && currentSection && lastMainNum) {
        lastSubLetter = m[1].toLowerCase();
        const name = `Q${lastMainNum}${lastSubLetter}`;
        const field = applyPending(this.buildParsedField(name, this.parseQuestionText(m[2]), line));
        currentSection.fields.push(field);
        result.totalFields++;
        lastField = field;
        continue;
      }

      // (8) Bullet point: "- ..." or "• ..."
      m = text.match(/^[-•]\s+(.+)/);
      if (m && currentSection && lastMainNum) {
        const existing = currentSection.fields.filter(f =>
          new RegExp(`^Q${lastMainNum}[a-z]$`).test(f.name)).length;
        const letter = String.fromCharCode('a'.charCodeAt(0) + existing);
        lastSubLetter = letter;
        const name = `Q${lastMainNum}${letter}`;
        const field = applyPending(this.buildParsedField(name, this.parseQuestionText(m[1]), line));
        currentSection.fields.push(field);
        result.totalFields++;
        lastField = field;
        continue;
      }

      // (9) [Removed] Indented-line-as-sub-question rule.
      //     Previously this caught indented PDF lines and turned them into Q{N}{letter}
      //     sub-questions. In practice it grabbed too much:
      //       - table column headers ("CETA heading (wherever", "Applicable) services")
      //       - secondary labels next to widgets ("Line II")
      //       - date format markers ("From (DD/MM/YYYY) To (DD/MM/YYYY)")
      //       - page footers ("Page # of ##")
      //     Each of those then bumped lastSubLetter, breaking subsequent paren markers.
      //     CRA-4 / Word docs that use explicit (a)/(b)/(i)/(ii) paren markers are
      //     fully handled by rule (4). Indented sub-questions without paren markers in
      //     Excel free-form go through parseFreeFormFormat, not parseTextWithStyles.

      // (10) Label-style line ending with ":" or "#" (e.g. "Name:", "Phone#").
      //      The colon is a pure separator and gets stripped, but "#" is part of
      //      the label's meaning (e.g., "Phone#" = "Phone Number") and is kept.
      if (currentSection && text.length <= 120 && /[:#]\s*$/.test(text)
          && !/[.!?]/.test(text.slice(0, -1))) {
        const cleaned = text.replace(/:\s*$/, '').trim();
        if (cleaned.length >= 2) {
          let name: string;
          if (lastMainNum) {
            const existing = currentSection.fields.filter(f =>
              new RegExp(`^Q${lastMainNum}[a-z]$`).test(f.name)).length;
            const letter = String.fromCharCode('a'.charCodeAt(0) + existing);
            lastSubLetter = letter;
            name = `Q${lastMainNum}${letter}`;
          } else {
            questionNum++;
            lastMainNum = String(questionNum);
            lastSubLetter = '';
            name = `Q${lastMainNum}`;
          }
          const field = applyPending(this.buildParsedField(name, this.parseQuestionText(cleaned), line));
          currentSection.fields.push(field);
          result.totalFields++;
          lastField = field;
          continue;
        }
      }

      // (11) Un-numbered text with explicit type hint → standalone question
      if (currentSection
          && /[-–]\s*(Text\s*Entry|Text\s*Input|Long\s*Text|Multi.?line|Text\s*Area|Radio|Dropdown|Drop\s*Down|Selection|Date|Numeric|File\s*Upload|Attachment|Check\s*Box)/i.test(text)) {
        questionNum++;
        lastMainNum = String(questionNum);
        lastSubLetter = '';
        const field = applyPending(this.buildParsedField(`Q${lastMainNum}`, this.parseQuestionText(text), line));
        currentSection.fields.push(field);
        result.totalFields++;
        lastField = field;
        continue;
      }

      // (12) Short text ending with "?" → standalone question
      if (currentSection && text.endsWith('?') && text.length <= 200) {
        questionNum++;
        lastMainNum = String(questionNum);
        lastSubLetter = '';
        const field = applyPending(this.buildParsedField(`Q${lastMainNum}`, this.parseQuestionText(text), line));
        currentSection.fields.push(field);
        result.totalFields++;
        lastField = field;
        continue;
      }

      // (NOTE) A "widget-bearing line" fallback was previously here, but it caused
      // collisions like "Form language English" (a UI radio toggle, not a question)
      // being numbered Q1 ahead of the real Q1. Better to let those lines fall
      // through — the user can add them manually if needed.
    }

    if (currentSection && currentSection.fields.length > 0) {
      result.sections.push(currentSection);
    }

    // ── Post-process: extract orphan option words from the start of one field's
    //    label and attach them as radio options to the PREVIOUS field.
    //    Example: a Word doc renders
    //      "Gender*:   ☐Male  ☐Female    Date of Birth*"
    //    which extracts as:
    //      field N    "Gender*"            (textbox/radio, no options)
    //      field N+1  "Male Female Date of Birth*"
    //    We move "Male Female" → field N's options, leaving "Date of Birth*" as N+1.
    const RADIO_OPTION_WORDS = /^(Male|Female|Yes|No|True|False|Other|Maybe|Always|Never|Sometimes|Agree|Disagree|Accept|Decline|Allow|Deny|Permit|Approve|Reject|Confirm|Resident|Domestic|Foreign|Individual|Corporate)$/i;
    for (const sec of result.sections) {
      for (let i = 0; i < sec.fields.length - 1; i++) {
        const cur = sec.fields[i];
        const next = sec.fields[i + 1];
        if (!next.label) continue;
        if (cur.options && cur.options.length > 0) continue;
        const words = next.label.split(/\s+/);
        let end = 0;
        for (let j = 0; j < words.length; j++) {
          if (RADIO_OPTION_WORDS.test(words[j])) end = j + 1;
          else break;
        }
        if (end >= 2) {
          const opts = words.slice(0, end);
          cur.type = 'radio';
          cur.options = opts;
          if (end === words.length) {
            // next field was ENTIRELY option words — drop it
            sec.fields.splice(i + 1, 1);
            result.totalFields--;
            i--;
          } else {
            next.label = words.slice(end).join(' ').trim();
          }
        }
      }
    }

    result.success = result.sections.length > 0 && result.totalFields > 0;
    if (!result.success) {
      result.errors.push('Could not detect sections or questions in the document');
      result.warnings.push('Ensure questions start with numbers (e.g., "1. Company Name") or use bold text for section headers');
    }
  }

  // ============================================================
  // PRIVATE: Build a ParsedField from text-derived data + widget hints
  // ------------------------------------------------------------
  // When the source line has a form widget (PDF annotation), the widget's type
  // and options take priority. Otherwise we fall back to text-pattern detection.
  // ============================================================

  private buildParsedField(
    name: string,
    parsed: { type: string; label: string; required: boolean; options: string[]; expression: number },
    line: TextLine
  ): ParsedField {
    let type = parsed.type;
    let options = parsed.options;
    let label = parsed.label;

    // Strip "DD/MM/YYYY" hints from label and force date type
    if (/\bDD\s*\/\s*MM\s*\/\s*YY/i.test(label)) {
      type = 'date';
      label = label.replace(/\s*\(?\s*DD\s*\/\s*MM\s*\/\s*YY+\s*\)?/gi, '').trim();
    }

    // PDF radio/checkbox exportValues are often opaque placeholders like "0"/"1"/"On"/"Off".
    // Prefer user-readable text-derived options whenever those are available.
    const widgetOptsLookOpaque = (opts?: string[]) =>
      !!opts && opts.length > 0 &&
      opts.every(o => /^(\s*|0|1|On|Off|Yes|No|True|False)$/i.test(o.trim()));

    // Widget data overrides text-based guess
    if (line.widgetType) {
      switch (line.widgetType) {
        case 'choice':
          type = 'dropdown';
          if (line.widgetOptions && line.widgetOptions.length > 0) {
            options = line.widgetOptions;
          }
          break;
        case 'radio':
          type = 'radio';
          // Prefer text-derived options when present (more readable than "0"/"1")
          if (options.length > 0 && widgetOptsLookOpaque(line.widgetOptions)) {
            // keep `options` from text parsing
          } else if (line.widgetOptions && line.widgetOptions.length > 0) {
            options = line.widgetOptions;
          } else if (!options.length) {
            options = ['Yes', 'No'];
          }
          break;
        case 'checkbox':
          type = 'checkbox';
          if (line.widgetOptions && line.widgetOptions.length > 0) {
            options = line.widgetOptions;
          }
          break;
        case 'date':
          type = 'date';
          break;
        case 'text':
          // Only override if we haven't already determined a richer type
          if (!['date', 'textarea', 'radio', 'dropdown', 'checkbox', 'fileupload'].includes(type)) {
            type = 'textbox';
          }
          break;
        case 'signature':
          type = 'textbox';
          break;
      }
    }

    return {
      name,
      label,
      type,
      required: parsed.required,
      options: options.length > 0 ? options : undefined,
      expression: parsed.expression
    };
  }

  // ============================================================
  // PRIVATE: Auto-detect conditional logic rules from field patterns
  // ============================================================

  private detectConditionalRules(result: ParseResult): void {
    // Collect all field names for lookup
    const allFields: { name: string; sectionIdx: number; fieldIdx: number }[] = [];
    for (let s = 0; s < result.sections.length; s++) {
      for (let f = 0; f < result.sections[s].fields.length; f++) {
        allFields.push({ name: result.sections[s].fields[f].name, sectionIdx: s, fieldIdx: f });
      }
    }

    // --- Step 1: Resolve skip targets for explicit rules from the Condition column ---
    for (const rule of result.conditionalRules) {
      if (!rule.skipToFieldName) {
        // Find the target field's position, then the field after it as the skip-to
        const targetIdx = allFields.findIndex(f => f.name === rule.targetFieldName);
        if (targetIdx >= 0 && targetIdx + 1 < allFields.length) {
          rule.skipToFieldName = allFields[targetIdx + 1].name;
        }
      }

      // Handle __SKIP_THIS__ marker from != expressions
      if (rule.skipToFieldName === '__SKIP_THIS__') {
        // Invert: the target becomes the skip destination, find the actual show target
        const targetIdx = allFields.findIndex(f => f.name === rule.targetFieldName);
        if (targetIdx >= 0 && targetIdx + 1 < allFields.length) {
          rule.skipToFieldName = allFields[targetIdx + 1].name;
        } else {
          rule.skipToFieldName = undefined;
        }
      }
    }

    // --- Step 2: Auto-detect patterns from field structure (only if no explicit rule exists) ---
    const explicitSources = new Set(result.conditionalRules.map(r => r.sourceFieldName));

    // Helper: any field that participates as a condition source MUST be required —
    // the show/hide logic can't work if the user can leave it blank.
    const markSourceMandatory = (fieldName: string) => {
      for (const sec of result.sections) {
        const f = sec.fields.find(x => x.name === fieldName);
        if (f) { f.required = true; return; }
      }
    };

    // ── CCRA-style detection ───────────────────────────────────────────────
    // Workbooks tagged by parseStructuredFormat with identifierGroup / isControl
    // metadata get a richer treatment than legacy Pattern C:
    //   • gateway-aware skip-to: a "gateway" is the FIRST field of an Identifier
    //     group when that field is itself a Yes/No scoping question (FCI1, CUI2).
    //     A gateway answered "No" skips the ENTIRE group to the next group's first
    //     field. Any other scoping Q only skips its own sub-letter children. (This
    //     is why Risk9 — preceded by the free-text Risk7/Risk8 — is NOT a gateway
    //     and Risk9=No advances to Risk10 rather than skipping all of Risk.)
    //   • Pattern D (sub-letter scoping): a Yes/No sub-letter Q like Q3.a gates
    //     subsequent sub-letter siblings up to (and including) the next Yes/No
    //     sibling — but ONLY when the immediately following sibling is a non-Yes/No
    //     detail field (date/text/score). Parallel Yes/No siblings (CUI2a/b/c) stay
    //     a flat list. "No" jumps past the entire sub-letter group.
    //   • transitive MandatoryDependantQuestion sets: gateway Yes branches list all
    //     controls in the control sub-table as deps (FCI1=Yes / CUI2=Yes).
    //
    // Heuristic to filter false-positive Yes/No detection on questions whose
    // Response Options cell says "Select Option" but whose label asks something
    // non-boolean ("What is the Confidence Level…"): require the label to start
    // with an interrogative auxiliary ("Do/Does/Did/Is/Are/Has/Have/Can/Will/…").
    // We deliberately do NOT treat a trailing "?" as sufficient — open "What/Which/
    // How…?" questions end with "?" too but are not Yes/No.
    const flatFields: ParsedField[] = [];
    for (const sec of result.sections) for (const f of sec.fields) flatFields.push(f);
    const isCcraStyle = flatFields.some(f => f.identifierGroup !== undefined || f.isControl);

    if (isCcraStyle) {
      const looksLikeYesNoLabel = (label: string): boolean => {
        const s = (label || '').trim();
        if (!s) return false;
        return /^(do|does|did|is|are|was|were|has|have|had|can|could|will|would|should|may|might|must|shall)\b/i.test(s);
      };
      const isYesNoScoping = (f: ParsedField): boolean => {
        if (!f) return false;
        if (f.type !== 'radio' && f.type !== 'dropdown') return false;
        if (!f.options || f.options.length !== 2) return false;
        const hasYesNo = f.options.includes('Yes') && f.options.includes('No');
        const hasTF = f.options.includes('True') && f.options.includes('False');
        if (!hasYesNo && !hasTF) return false;
        return looksLikeYesNoLabel(f.label);
      };
      const yesValueOf = (f: ParsedField): string | null =>
        f.options?.includes('Yes') ? 'Yes' : f.options?.includes('True') ? 'True' : null;

      const isTopLevelQ = (f: ParsedField): boolean =>
        !f.isControl && !!f.numberPart && /^\d+(?:\.\d+)*$/.test(f.numberPart);
      const isSubLetterQ = (f: ParsedField): boolean =>
        !f.isControl && !!f.numberPart && /^\d+(?:\.\d+)*[a-z]$/.test(f.numberPart);
      const parentNumOf = (f: ParsedField): string =>
        (f.numberPart || '').replace(/[a-z]$/, '');

      // Build group order + gateway map. A group's gateway is its FIRST field,
      // and only counts as a gateway when that first field is itself a top-level
      // Yes/No scoping question. (Groups whose first field is free text — e.g. Risk
      // starting with Risk7/Risk8 — have no gateway, so their later scoping Q's only
      // skip their own children on "No".)
      const groupOrder: string[] = [];
      const firstFieldByGroup = new Map<string, string>();
      const gatewayByGroup = new Map<string, string>();
      for (const f of flatFields) {
        if (f.isControl || !f.identifierGroup) continue;
        if (!groupOrder.includes(f.identifierGroup)) {
          groupOrder.push(f.identifierGroup);
          firstFieldByGroup.set(f.identifierGroup, f.name);
          if (isTopLevelQ(f) && isYesNoScoping(f)) {
            gatewayByGroup.set(f.identifierGroup, f.name);
          }
        }
      }

      const controlNames = flatFields.filter(f => f.isControl).map(f => f.name);

      // Convenience lookups by name and by index.
      const indexByName = new Map<string, number>();
      flatFields.forEach((f, i) => indexByName.set(f.name, i));

      // A sub-letter Yes/No question is a "Pattern D source" (it gates its own
      // siblings) when the sibling immediately after it is a non-Yes/No detail
      // field. Those siblings are owned by the Pattern D rule, so a parent
      // top-level scoping Q must NOT also list them as direct deps — it reveals
      // only up to (and including) that nested gate. CUI3=Yes therefore reveals
      // just CUI3a, and CUI3a=Yes reveals CUI3b/c/d.
      const isPatternDSource = (f: ParsedField): boolean => {
        if (!isSubLetterQ(f) || !isYesNoScoping(f)) return false;
        const idx = indexByName.get(f.name);
        if (idx === undefined) return false;
        const next = flatFields[idx + 1];
        if (!next || next.isControl) return false;
        const pp = parentNumOf(f);
        const nextIsSibling = !!next.numberPart && next.numberPart.startsWith(pp) && /[a-z]$/.test(next.numberPart);
        return nextIsSibling && !isYesNoScoping(next);
      };

      // Find first non-control, non-sub-letter-of-X field after index i, optionally
      // skipping anything whose numberPart starts with `parentPrefix`.
      const nextOutsidePrefix = (startIdx: number, parentPrefix: string | null): string | undefined => {
        for (let j = startIdx; j < flatFields.length; j++) {
          const fj = flatFields[j];
          if (fj.isControl) continue;
          if (parentPrefix && fj.numberPart && fj.numberPart.startsWith(parentPrefix)
              && /[a-z]$/.test(fj.numberPart)) continue;
          return fj.name;
        }
        return undefined;
      };

      // ── Guidance-driven reveals ────────────────────────────────────────────
      // Some templates state the trigger explicitly in the Guidance column, e.g.
      // "↳ Shown when Q1 = No" / "Shown when Q2 = Yes". When that convention is
      // present it is authoritative — it captures triggers the structural
      // heuristics can't infer (a child revealed on "No", or only some siblings
      // revealed on Yes and others on No). We parse it and build precise rules,
      // skipping the structural Pattern passes entirely for this form.
      const shownWhenRe = /shown\s+when\s+Q?\s*([0-9]+(?:[.\-][0-9a-z]+)?)\s*(?:=|is|:)?\s*["']?(yes|no|true|false)\b/i;
      const normNum = (s: string) => s.replace(/^(\d+(?:\.\d+)*)[.\-]([a-z])$/i, '$1$2');
      const guidanceReveals: { child: ParsedField; parentNum: string; trigger: string }[] = [];
      for (const f of flatFields) {
        if (f.isControl) continue;
        const m = shownWhenRe.exec(f.helpText || '');
        if (m) {
          const t = m[2].toLowerCase();
          guidanceReveals.push({
            child: f,
            parentNum: normNum(m[1]),
            trigger: t === 'yes' ? 'Yes' : t === 'no' ? 'No' : t === 'true' ? 'True' : 'False'
          });
        }
      }
      const useGuidance = guidanceReveals.length >= 3;

      if (useGuidance) {
        // Group children by (parent, trigger) → one reveal condition each.
        const groups = new Map<string, { parent: ParsedField; trigger: string; children: string[] }>();
        for (const gr of guidanceReveals) {
          const parent = flatFields.find(f => !f.isControl && f.numberPart === gr.parentNum);
          if (!parent) continue;
          const key = `${parent.name}|${gr.trigger}`;
          if (!groups.has(key)) groups.set(key, { parent, trigger: gr.trigger, children: [] });
          groups.get(key)!.children.push(gr.child.name);
        }
        for (const { parent, trigger, children } of groups.values()) {
          const yn = isYesNoScoping(parent);
          result.conditionalRules.push({
            sourceFieldName: parent.name,
            triggerValue: yn ? trigger : '',
            targetFieldName: children[0],
            mandatoryDeps: children,
            revealOnAnyAnswer: yn ? undefined : true
          });
          markSourceMandatory(parent.name);
        }
        // parents handled here are explicit sources; their children are owned.
        for (const { parent } of groups.values()) explicitSources.add(parent.name);
      }

      // Pattern C (CCRA-flavoured): top-level Yes/No Q with sub-letter children.
      if (!useGuidance) for (let i = 0; i < flatFields.length; i++) {
        const f = flatFields[i];
        if (f.isControl || !f.identifierGroup) continue;
        if (explicitSources.has(f.name)) continue;
        if (!isTopLevelQ(f)) continue;
        if (!isYesNoScoping(f)) continue;

        // Gateways are handled by the dedicated gateway pass below. A gateway
        // gates the ENTIRE downstream span up to the next gateway (not just its
        // own sub-letter children), so the per-question Pattern C rule built here
        // would under-hide it (the "too many questions visible on load" bug).
        if (gatewayByGroup.get(f.identifierGroup) === f.name) continue;

        // Collect sub-letter children (stop at the next top-level Q OR a control row).
        const parentNum = f.numberPart!;
        const children: ParsedField[] = [];
        for (let j = i + 1; j < flatFields.length; j++) {
          const fj = flatFields[j];
          if (fj.isControl) break;
          if (fj.numberPart && fj.numberPart.startsWith(parentNum)
              && /[a-z]$/.test(fj.numberPart)) {
            children.push(fj);
          } else {
            break;
          }
        }
        if (children.length === 0) continue;

        const yesVal = yesValueOf(f)!;
        const isGateway = gatewayByGroup.get(f.identifierGroup) === f.name;

        // Skip-to (No destination):
        //   gateway          → first field of NEXT Identifier group
        //   non-gateway      → next top-level Q (skipping our own sub-letter children)
        let skipTo: string | undefined;
        if (isGateway) {
          const groupIdx = groupOrder.indexOf(f.identifierGroup);
          const nextGroup = groupOrder[groupIdx + 1];
          if (nextGroup) skipTo = firstFieldByGroup.get(nextGroup);
        } else {
          const lastChildIdx = indexByName.get(children[children.length - 1].name)!;
          skipTo = nextOutsidePrefix(lastChildIdx + 1, null);
        }

        // Mandatory deps:
        //   • direct sub-letter children, truncated at the first nested (Pattern D)
        //     gate so we don't double-reveal questions the nested rule owns,
        //   • plus all controls — but ONLY for a gateway Yes branch. The control
        //     sub-table is unlocked by the umbrella scoping question of a group
        //     (FCI1=Yes, CUI2=Yes); a non-gateway scoping Q (Risk10, CUI3, …) only
        //     reveals its own children.
        const depChildren: string[] = [];
        for (const c of children) {
          depChildren.push(c.name);
          if (isPatternDSource(c)) break;   // nested gate owns the remaining siblings
        }
        const deps = [...depChildren];
        if (isGateway) {
          for (const cn of controlNames) deps.push(cn);
        }

        result.conditionalRules.push({
          sourceFieldName: f.name,
          triggerValue: yesVal,
          targetFieldName: children[0].name,
          skipToFieldName: skipTo,
          mandatoryDeps: deps
        });
        explicitSources.add(f.name);
        markSourceMandatory(f.name);
      }

      // Pattern D (CCRA-flavoured): sub-letter Yes/No Q gates subsequent siblings.
      if (!useGuidance) for (let i = 0; i < flatFields.length; i++) {
        const f = flatFields[i];
        if (f.isControl || !f.identifierGroup) continue;
        if (explicitSources.has(f.name)) continue;
        if (!isSubLetterQ(f)) continue;
        if (!isYesNoScoping(f)) continue;

        const parentPrefix = parentNumOf(f);
        const siblings: ParsedField[] = [];
        for (let j = i + 1; j < flatFields.length; j++) {
          const fj = flatFields[j];
          if (fj.isControl) break;
          if (fj.numberPart && fj.numberPart.startsWith(parentPrefix)
              && /[a-z]$/.test(fj.numberPart)) {
            siblings.push(fj);
          } else {
            break;
          }
        }
        if (siblings.length === 0) continue;

        // Only nest when the IMMEDIATELY following sibling is a non-Yes/No detail
        // field (date / text / score). When the next sibling is itself a Yes/No
        // question the siblings are parallel (e.g. CUI2a/b/c — three independent
        // "Have you…?" questions all revealed together by the parent), so we leave
        // them in the flat linear chain rather than chaining a→b→c.
        if (isYesNoScoping(siblings[0])) continue;

        // Deps run up to AND INCLUDING the next sub-letter Yes/No sibling.
        const deps: string[] = [];
        for (const s of siblings) {
          deps.push(s.name);
          if (isYesNoScoping(s)) break;
        }

        // "No" skips past the entire sub-letter group → first field whose
        // numberPart does NOT start with parentPrefix (or is non-sub-letter).
        const lastIdx = indexByName.get(f.name)!;
        const skipTo = nextOutsidePrefix(lastIdx + 1, parentPrefix);

        const yesVal = yesValueOf(f)!;
        result.conditionalRules.push({
          sourceFieldName: f.name,
          triggerValue: yesVal,
          targetFieldName: siblings[0].name,
          skipToFieldName: skipTo,
          mandatoryDeps: deps
        });
        explicitSources.add(f.name);
        markSourceMandatory(f.name);
      }

      // ── Pass ANY: non-Yes/No top-level parent with sub-letter children ─────
      // A top-level question that is NOT a Yes/No scoping question but DOES have
      // sub-letter children (e.g. a dropdown "What is your highest education?" →
      // 2a/2b) reveals those children once it is answered with ANY value. This
      // keeps the children hidden on load (they land in a rule's dependant set)
      // while leaving the parent itself visible.
      if (!useGuidance) for (let i = 0; i < flatFields.length; i++) {
        const f = flatFields[i];
        if (f.isControl || !f.identifierGroup) continue;
        if (explicitSources.has(f.name)) continue;
        if (!isTopLevelQ(f)) continue;
        if (isYesNoScoping(f)) continue;   // Yes/No top-levels: Pattern C / gateway

        const parentNum = f.numberPart!;
        const children: ParsedField[] = [];
        for (let j = i + 1; j < flatFields.length; j++) {
          const fj = flatFields[j];
          if (fj.isControl) break;
          if (fj.numberPart && fj.numberPart.startsWith(parentNum) && /[a-z]$/.test(fj.numberPart)) {
            children.push(fj);
          } else {
            break;
          }
        }
        if (children.length === 0) continue;

        const depChildren: string[] = [];
        for (const c of children) {
          depChildren.push(c.name);
          if (isPatternDSource(c)) break;   // nested gate owns the rest
        }

        result.conditionalRules.push({
          sourceFieldName: f.name,
          triggerValue: '',                 // any value reveals (see revealOnAnyAnswer)
          targetFieldName: children[0].name,
          mandatoryDeps: depChildren,
          revealOnAnyAnswer: true
        });
        explicitSources.add(f.name);
        markSourceMandatory(f.name);
      }

      // ── Gateway pass (own-group reach) ─────────────────────────────────────
      // A gateway is the first field of an Identifier group when that field is a
      // top-level Yes/No scoping question (FCI1, CUI2, …). Answering a gateway
      // "Yes" reveals the REST OF ITS OWN identifier group (up to the next group);
      // "No" skips to the first field of the next group. Its mandatory-dependant
      // set is that own-group reach MINUS any question already revealed by another
      // rule (those belong to their own parent). There is NO cross-group
      // absorption — every group's first question stays visible on load.
      //
      // Controls are attached only to gateways that own a sub-letter sub-table
      // (so the control sub-table is unlocked by a scoping question that has its
      // own detail questions), preserving the existing control behaviour.

      // Questions revealed by the non-gateway rules built above — a gateway must
      // not re-list these.
      if (!useGuidance) {
      const ownedByOtherRules = new Set<string>();
      for (const r of result.conditionalRules) {
        if (r.targetFieldName) ownedByOtherRules.add(r.targetFieldName);
        if (r.mandatoryDeps) {
          for (const d of r.mandatoryDeps) if (d) ownedByOtherRules.add(d);
        }
      }

      // Gateway field indices in document order.
      const gatewayIdxs: number[] = [];
      flatFields.forEach((f, i) => {
        if (!f.isControl && f.identifierGroup
            && gatewayByGroup.get(f.identifierGroup) === f.name) {
          gatewayIdxs.push(i);
        }
      });

      for (const gi of gatewayIdxs) {
        const gw = flatFields[gi];
        if (explicitSources.has(gw.name)) continue;

        // Direct sub-letter children of the gateway (e.g. FCI1a; CUI2a/b/c).
        const gwNum = gw.numberPart || '';
        let hasChildren = false;
        let firstChild: string | undefined;
        for (let j = gi + 1; j < flatFields.length; j++) {
          const fj = flatFields[j];
          if (fj.isControl) break;
          if (gwNum && fj.numberPart && fj.numberPart.startsWith(gwNum) && /[a-z]$/.test(fj.numberPart)) {
            hasChildren = true;
            if (!firstChild) firstChild = fj.name;
          } else {
            break;
          }
        }

        // Reach: the rest of the gateway's OWN identifier group (stop at the first
        // field of a different group), excluding controls and anything already
        // owned by another rule.
        const reachDeps: string[] = [];
        for (let j = gi + 1; j < flatFields.length; j++) {
          const fj = flatFields[j];
          if (fj.isControl || !fj.identifierGroup) continue;
          if (fj.identifierGroup !== gw.identifierGroup) break;   // left the group
          if (ownedByOtherRules.has(fj.name)) continue;
          reachDeps.push(fj.name);
        }
        if (reachDeps.length === 0) continue;   // group has no extra fields to gate

        const deps = [...reachDeps];
        if (hasChildren) {
          for (const cn of controlNames) deps.push(cn);
        }

        // "No" destination: the first field of the next identifier group (which
        // is itself visible on load), else nothing.
        const gIdx = groupOrder.indexOf(gw.identifierGroup!);
        const nextGroup = groupOrder[gIdx + 1];
        const skipTo = nextGroup ? firstFieldByGroup.get(nextGroup) : undefined;

        result.conditionalRules.push({
          sourceFieldName: gw.name,
          triggerValue: yesValueOf(gw)!,
          targetFieldName: firstChild ?? reachDeps[0],
          skipToFieldName: skipTo,
          mandatoryDeps: deps
        });
        explicitSources.add(gw.name);
        markSourceMandatory(gw.name);
      }
      } // end if (!useGuidance) — gateway pass

      // ── Coverage pass ──────────────────────────────────────────────────────
      // Every row hidden in the source must be revealable by SOME rule, or it
      // stays hidden forever. Any hidden field not already revealed above is
      // attached to the nearest preceding VISIBLE field (its anchor) — answering
      // that anchor reveals the run of otherwise-orphaned hidden fields. This is
      // what reveals an ungated hidden group with no scoping gateway of its own
      // (e.g. CCRA Risk7–Risk12, revealed by the preceding visible ICT6).
      const covered = new Set<string>();
      for (const r of result.conditionalRules) {
        if (r.targetFieldName) covered.add(r.targetFieldName);
        if (r.mandatoryDeps) for (const d of r.mandatoryDeps) if (d) covered.add(d);
      }
      const orphansByAnchor = new Map<string, string[]>();
      let anchorName: string | undefined;
      for (const f of flatFields) {
        if (!f.initiallyHidden) { anchorName = f.name; continue; }  // visible → anchor
        if (covered.has(f.name)) continue;                          // already revealed
        if (!anchorName) continue;                                  // nothing before it
        if (!orphansByAnchor.has(anchorName)) orphansByAnchor.set(anchorName, []);
        orphansByAnchor.get(anchorName)!.push(f.name);
      }
      for (const [aName, deps] of orphansByAnchor) {
        const anchor = flatFields.find(f => f.name === aName)!;
        const existing = result.conditionalRules.find(r => r.sourceFieldName === aName);
        if (existing) {
          existing.mandatoryDeps = [...(existing.mandatoryDeps || []), ...deps];
        } else {
          const yn = isYesNoScoping(anchor);
          result.conditionalRules.push({
            sourceFieldName: aName,
            triggerValue: yn ? (yesValueOf(anchor) || 'Yes') : '',
            targetFieldName: deps[0],
            mandatoryDeps: deps,
            revealOnAnyAnswer: !yn
          });
          explicitSources.add(aName);
          markSourceMandatory(aName);
        }
      }
    }

    for (const section of result.sections) {
      const fields = section.fields;
      for (let i = 0; i < fields.length; i++) {
        const field = fields[i];
        const next = fields[i + 1];

        if (explicitSources.has(field.name)) continue;

        // Pattern A: Yes/No or True/False radio immediately followed by a
        // `<name>_Comments` textarea → show the comments only when the positive
        // value is selected. Skip-to target = the next field that's not part of
        // this comment chain.
        if (next
            && field.type === 'radio'
            && field.options?.length === 2
            && next.type === 'textarea'
            && next.name === `${field.name}_Comments`)
        {
          let triggerValue: string | null = null;
          if (field.options.includes('True'))      triggerValue = 'True';
          else if (field.options.includes('Yes'))  triggerValue = 'Yes';

          if (triggerValue) {
            let skipTo: string | undefined;
            for (let j = i + 2; j < fields.length; j++) {
              if (!fields[j].name.endsWith('_Comments')) {
                skipTo = fields[j].name;
                break;
              }
            }
            result.conditionalRules.push({
              sourceFieldName: field.name,
              triggerValue,
              targetFieldName: next.name,
              skipToFieldName: skipTo
            });
            markSourceMandatory(field.name);
          }
        }

        // Pattern B: explicit "skip to QN" / "go to QN" instruction inside the label
        //   e.g.,  "If No, skip to Q5"
        const skipMatch = field.label?.match(/\bskip\s+to\s+(Q[\w.\-]+)/i)
          || field.label?.match(/\bgo\s+to\s+(Q[\w.\-]+)/i);
        if (skipMatch && field.type === 'radio' && field.options?.length) {
          const targetName = skipMatch[1];
          // Trigger value = whichever option the label says ("If No, ...")
          const ifMatch = field.label.match(/\bIf\s+(\w+)\s*[,:]?\s*(skip|go)/i);
          const triggerValue = ifMatch
            ? (field.options.find(o => o.toLowerCase() === ifMatch[1].toLowerCase()) || ifMatch[1])
            : (field.options.find(o => /no|false/i.test(o)) || field.options[1]);
          const targetField = fields.find(f => f.name === targetName);
          if (targetField) {
            result.conditionalRules.push({
              sourceFieldName: field.name,
              triggerValue,
              targetFieldName: targetName,
              skipToFieldName: '__SKIP_THIS__'
            });
            markSourceMandatory(field.name);
          }
        }

        // Pattern C: A top-level Yes/No or True/False radio Q{N} followed by sub-letter
        //   children Q{N}a, Q{N}b, … → Yes/True shows the children; No/False skips
        //   past all of them to the next top-level field. Catches the classic
        //   "Is any 3TG used? → show metal sub-questions" pattern.
        //
        //   Also auto-converts an unanswered "?"-ending question to Yes/No when its
        //   children exist, because most forms use this implicit pattern.
        const numMatch = field.name.match(/^Q(\d+(?:[.\-]\d+)*)$/);
        if (numMatch) {
          const parentNum = numMatch[1];
          const childPattern = new RegExp(`^Q${this.escapeRegex(parentNum)}[a-z]+$`);
          const childIdxStart = i + 1;
          let childIdxEnd = childIdxStart;
          while (childIdxEnd < fields.length && childPattern.test(fields[childIdxEnd].name)) {
            childIdxEnd++;
          }
          const children = fields.slice(childIdxStart, childIdxEnd);

          if (children.length > 0) {
            // If the parent isn't already a Yes/No radio but ends with "?", treat as one
            const isAlreadyRadio = field.type === 'radio'
              && field.options?.length === 2
              && (field.options.includes('Yes') || field.options.includes('True'));
            const looksLikeYesNoQ = /\?\s*$/.test(field.label || '');

            if (!isAlreadyRadio && looksLikeYesNoQ) {
              field.type = 'radio';
              field.options = ['Yes', 'No'];
            }

            if (field.type === 'radio' && field.options?.length === 2) {
              const yesValue = field.options.includes('Yes') ? 'Yes'
                : field.options.includes('True') ? 'True'
                : null;
              const skipTarget = fields[childIdxEnd];  // field after the last child

              if (yesValue) {
                // First child becomes the visible target on Yes
                result.conditionalRules.push({
                  sourceFieldName: field.name,
                  triggerValue: yesValue,
                  targetFieldName: children[0].name,
                  skipToFieldName: skipTarget?.name
                });
                markSourceMandatory(field.name);
              }
            }
          }
        }
      }
    }
  }

  // ============================================================
  // PRIVATE: Convert ParsedSection → SectionInfo (with HTML)
  // ============================================================

  private convertToSectionInfo(section: ParsedSection, templateId: string, sequence: number): SectionInfo {
    const sectionInfo = new SectionInfo();
    sectionInfo.UniqueID = this.generateUUID();
    sectionInfo.TemplateID = templateId;
    sectionInfo.Title = section.title;
    sectionInfo.Description = section.description || '';
    sectionInfo.Sequence = sequence;
    sectionInfo.DisplayType = 'One';
    sectionInfo.CreatedBy = { UniqueID: localStorage.getItem('UserId') || '' };
    sectionInfo.UpdatedBy = { UniqueID: localStorage.getItem('UserId') || '' };

    sectionInfo.Fields = section.fields.map((field, index) => {
      const fq = new FormQuestions();
      fq.UniqueID = this.generateUUID();
      fq.Name = field.name;
      const aiType = this.resolveQuestionType(field.type);
      // The platform has NO renderable "content-only" field TYPE. The manual builder's
      // "Text" content element is saved as a normal TextInput whose Label holds the full
      // rich content and whose QuestionHtml is that content as HTML (a raw "Info" type
      // does NOT render in the preview). So a content block (intro / instructions) is
      // saved the same way: Type TextInput, rich QuestionHtml, not mappable/required.
      const isContent = aiType === 'Info' || aiType === 'GuidanceText' || aiType === 'TextLabel';
      // Content blocks are saved as a TextArea with a HIDDEN input (display:none) — the
      // platform requires every field to carry an input, so we include one but hide it
      // (mirrors the manual builder's "Hidden" checkbox). The rich QuestionHtml shows;
      // no visible textbox.
      fq.Type = isContent ? 'TextArea' : aiType;
      // Label keeps the model's text/markup/HTML as-is; generateQuestionHtml (regular)
      // and the content path below render it safely (sanitize HTML / convert markdown).
      // Only OPTIONS stay strictly plain (stripped below).
      fq.Label = field.label || '';
      fq.Required = isContent ? false : field.required;
      fq.IsMappable = !isContent;
      fq.FieldType = 'IndustryData';            // match the manual builder's FieldType
      if (isContent) (fq as any).__contentBlock = true;   // marks it for numbering exclusion
      fq.DisplayOrder = index + 1;
      fq.HelpContent = field.helpText || '';
      this.applyGuidance(fq, field.helpText);   // guidance block (manual shape)
      // Expression 999 = the platform's "no validation" id (what the manual builder
      // stamps on a plain field); use the AI's specific expression only when present.
      // Content blocks carry no validation (their input is hidden) → 0, as the manual.
      fq.Expression = isContent ? 0 : (field.expression || 999);
      fq.MinChar = field.minLength ?? null;
      fq.MaxChar = field.maxLength ?? null;

      // Build Options (plain text — strip any markup too). Content blocks have none.
      if (!isContent && field.options && field.options.length > 0) {
        fq.Options = field.options.map(opt => this.makeOption(this.stripInlineMarkup(opt)));
      }

      // Safety net: a radio / checkbox / dropdown renders its CONTROL from its
      // options — with none it shows only the label. Single tick-box / yes-no
      // questions often arrive without options, so synthesise sensible defaults.
      if (!isContent && (!fq.Options || fq.Options.length === 0)) {
        if (fq.Type === 'RadioButton' || fq.Type === 'DropDownListSelection') {
          fq.Options = ['Yes', 'No'].map(o => this.makeOption(o));
        } else if (fq.Type === 'CheckBox') {
          fq.Options = ['Yes'].map(o => this.makeOption(o));
        }
      }

      // Generate HTML. Content blocks: rich QuestionHtml (headings/bullets/links) — the
      // manual "Text" element renders its content this way.
      // Content blocks: the Label holds the LLM's rich content — real HTML (sanitized)
      // when the model emitted HTML, else its markdown converted. Regular fields: plain.
      const contentInner = this.looksLikeHtml(fq.Label) ? this.sanitizeHtml(fq.Label) : this.formatRichHtml(fq.Label);
      fq.QuestionHtml = isContent
        ? `<div id="label_${fq.UniqueID}" maxlength="40">${contentInner}</div>`
        : this.generateQuestionHtml(fq);
      // Content blocks: a HIDDEN textarea (display:none) — matches the manual builder's
      // "Hidden" option so the page shows content only, with no visible input box.
      fq.AnswerHtml = isContent
        ? `\n      <div style='text-align:left'>\n        <textarea type="text" id="textareaID_${fq.UniqueID}" style="display:none"></textarea>\n      </div><br/>\n      `
        : this.generateAnswerHtml(fq);

      // The rendered form uses QuestionHtml (built above). Store the Label as PLAIN TEXT
      // for content blocks so surfaces that print the label as text (Attribute Mapper
      // grid, section lists) don't show raw HTML tags. Cap length so long intro/PRA text
      // stays readable in those grids.
      if (isContent) {
        const plain = this.htmlToPlainText(fq.Label);
        fq.Label = plain.length > 120 ? plain.slice(0, 117) + '…' : plain;
      }

      return fq;
    });

    return sectionInfo;
  }

  // ============================================================
  // PRIVATE: Resolve a field type to a form-builder canonical type
  // ============================================================
  // The classic parser emits loose words ("radio", "dropdown"); the AI emits the
  // backend FormQuestionType values ("BooleanSelection", "RadioButtonSelection",
  // "DropDownListSelection"…). Both must map onto the types generateAnswerHtml +
  // the form-builder actually render: TextInput, TextArea, RadioButton, CheckBox,
  // DropDownListSelection, Date, Attachment, GuidanceText, TextLabel.
  private makeOption(text: string): OptionValues {
    const ov = new OptionValues();
    ov.UniqueID = this.generateUUID();
    ov.OptionText = text;
    ov.OptionValue = text;
    ov.Score = '';
    return ov;
  }

  // Remove lightweight markup markers so plain-text surfaces (labels, options) never
  // show literal **, *, __, or [text](url) — the model is told to keep these plain,
  // but strip defensively in case it adds emphasis (e.g. *italic* on bilingual text).
  private stripInlineMarkup(s: string): string {
    return this.stripTags(s)                                      // DOM-based tag removal (safe)
      .replace(/\[([^\]]+)\]\((?:https?:\/\/[^\s)]+)\)/g, '$1')  // [text](url) -> text
      .replace(/\*\*([^*]+)\*\*/g, '$1')                          // **bold**
      .replace(/__([^_]+)__/g, '$1')                              // __underline__
      .replace(/\*([^*\n]+)\*/g, '$1')                            // *italic*
      .trim();
  }

  private resolveQuestionType(raw: string): string {
    if (!raw) return 'TextInput';

    // 1) classic loose words (exact key in TYPE_MAP)
    const exact = TYPE_MAP[raw.toLowerCase()];
    if (exact) return exact;

    // 2) backend value / canonical heuristics (order matters)
    const t = raw.toLowerCase().replace(/[^a-z]/g, '');
    if (t.includes('multiselect') || t.includes('multi')) return 'CheckBox';
    if (t.includes('check')) return 'CheckBox';
    if (t.includes('dropdown') || t.includes('select')) return 'DropDownListSelection';
    if (t.includes('radio') || t.includes('boolean') || t.includes('yesno') || t.includes('truefalse')) return 'RadioButton';
    if (t.includes('textarea') || t.includes('paragraph') || t.includes('multiline')) return 'TextArea';
    if (t.includes('date')) return 'Date';
    if (t.includes('attach') || t.includes('file') || t.includes('upload')) return 'Attachment';
    if (t.includes('info') || t.includes('introduction') || t.includes('instruction')) return 'Info';
    if (t.includes('guidance')) return 'GuidanceText';
    if (t.includes('label')) return 'TextLabel';
    if (t.includes('text') || t.includes('input') || t.includes('string')) return 'TextInput';

    return 'TextInput';
  }

  // ============================================================
  // PRIVATE: Per-question guidance — mirror the MANUAL form's exact shape.
  // ============================================================
  // From a real manual SaveSection payload, a question with guidance stores:
  //   GuidanceText = "[alignment]:::<plain text>"  (preview reads split(':::')[0])
  //   GuidanceHtml = the rich-text editor's HTML, rendered verbatim as innerHTML.
  //   DisplayHelp  = false
  // The manual editor emits one <div> per line, each wrapped in <font face="Arial">,
  // bare URLs as clickable <a target="_blank"> anchors, and a blank line as
  // <div><font face="Arial"><br></font></div>. We mirror that exactly so AI-built
  // guidance is byte-compatible with the manual form (and the preview renders links,
  // which it shows verbatim — no auto-linkify). A leading "Note:"/"NOTA:" is bolded.
  // Convert lightweight markup (what the AI is told to emit) into the manual editor's
  // HTML shape: one <div><font face="Arial">…</font></div> per line, "- " bullets into
  // <ul><li>, **bold**, __underline__, *italic*, [text](url) and bare URLs into
  // clickable <a target="_blank"> anchors, leading Note:/NOTA: bolded. Everything is
  // HTML-escaped first, so only these markers become tags (no raw-HTML injection).
  // Inline markup → HTML for a single run of text: escape, then [text](url) / bare URL
  // links, **bold**, __underline__, *italic*, and a leading Note:/NOTA: bolded.
  private inlineMarkup(s: string): string {
    const esc = (x: string) => (x || '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    let out = esc(s);
    out = out.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank">$1</a>'); // [text](url)
    out = out.replace(/(^|[^"'=>])(https?:\/\/[^\s<]+)/g, '$1<a href="$2" target="_blank">$2</a>');     // bare url
    out = out.replace(/\*\*([^*]+)\*\*/g, '<b>$1</b>');   // **bold**
    out = out.replace(/__([^_]+)__/g, '<u>$1</u>');        // __underline__
    out = out.replace(/\*([^*\n]+)\*/g, '<i>$1</i>');      // *italic* (after bold)
    out = out.replace(/^(Note\s*:?|NOTA\s*:?)/i, '<b>$1</b>');
    return out;
  }

  private formatRichHtml(text: string): string {
    const raw = (text || '').replace(/\r\n/g, '\n').trim();
    if (!raw) return '';
    const parts: string[] = [];
    let inList = false;
    for (const line of raw.split('\n')) {
      const t = line.trim();
      const bullet = /^[-*]\s+(.*)$/.exec(t);
      if (bullet) {
        if (!inList) { parts.push('<ul>'); inList = true; }
        parts.push(`<li><font face="Arial">${this.inlineMarkup(bullet[1])}</font></li>`);
        continue;
      }
      if (inList) { parts.push('</ul>'); inList = false; }
      parts.push(t ? `<div><font face="Arial">${this.inlineMarkup(t)}</font></div>` : '<div><font face="Arial"><br></font></div>');
    }
    if (inList) parts.push('</ul>');
    return parts.join('');
  }

  // True when the text contains real HTML tags (the model emitted HTML, not markdown).
  private looksLikeHtml(s: string): boolean {
    return /<\/?[a-z][^>]*>/i.test(s || '');
  }

  // Allowlist sanitizer. GuidanceHtml/QuestionHtml are rendered as raw innerHTML
  // (the safeHtml pipe bypasses Angular's sanitizer), so LLM-generated HTML MUST be
  // sanitized here: keep only safe formatting tags + attributes; drop scripts, event
  // handlers, inline styles, and any non-http(s) link. Disallowed tags are unwrapped
  // (their text is kept, the tag removed) so no content is lost.
  private sanitizeHtml(html: string): string {
    // Formatting tags allowed for nicer layout: headings, paragraphs, lists, rules,
    // centering. NO script/style-vector tags.
    const ALLOWED = new Set(['B','STRONG','I','EM','U','A','UL','OL','LI','BR','HR','DIV','P','SPAN','FONT','CENTER','H1','H2','H3','H4']);
    const ATTRS: Record<string, Set<string>> = { A: new Set(['href','target']), FONT: new Set(['face']) };
    const escText = (s: string) => s.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
    const escAttr = (s: string) => s.replace(/&/g,'&amp;').replace(/"/g,'&quot;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
    const walk = (node: Node): string => {
      let out = '';
      node.childNodes.forEach((child: any) => {
        if (child.nodeType === 3) { out += escText(child.textContent || ''); return; }   // text
        if (child.nodeType !== 1) return;
        const el = child as HTMLElement;
        const tag = el.tagName.toUpperCase();
        const inner = walk(el);
        if (!ALLOWED.has(tag)) { out += inner; return; }   // drop tag, keep its text
        let attrs = '';
        const allow = ATTRS[tag];
        Array.from(el.attributes).forEach((a: any) => {
          const n = a.name.toLowerCase();
          const v = a.value ?? '';
          if (allow && allow.has(n)) {
            if (n === 'href' && !/^https?:\/\//i.test(v)) return;   // http(s) links only
            attrs += ` ${n}="${escAttr(v)}"`;
            return;
          }
          // Safe, tag-agnostic layout attributes: alignment only (no script vector).
          if (n === 'align' && /^(left|right|center|justify)$/i.test(v)) {
            attrs += ` align="${v.toLowerCase()}"`;
          } else if (n === 'style') {
            const m = /^\s*text-align\s*:\s*(left|right|center|justify)\s*;?\s*$/i.exec(v);
            if (m) attrs += ` style="text-align:${m[1].toLowerCase()}"`;   // only text-align survives
          }
        });
        if (tag === 'A' && !/\starget=/.test(attrs)) attrs += ' target="_blank"';
        const t = tag.toLowerCase();
        out += (tag === 'BR' || tag === 'HR') ? `<${t}${attrs}>` : `<${t}${attrs}>${inner}</${t}>`;
      });
      return out;
    };
    try {
      const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
      const root = doc.body.firstChild as Node;
      return root ? walk(root) : '';
    } catch { return this.formatRichHtml(html); }
  }

  // Strip all tags to plain text (for GuidanceText, which is the alignment-prefixed
  // plain mirror of GuidanceHtml).
  private htmlToPlainText(html: string): string {
    return this.stripTags(html).replace(/\s+/g, ' ').trim();
  }

  // Reduce HTML to plain text SAFELY. The DOM parser fully tokenizes the input, so
  // unclosed or nested tags (e.g. "<script src=//x" with no closing ">") cannot
  // survive — unlike a single-pass /<[^>]+>/ strip. The catch is a defensive fallback
  // for the rare case DOMParser is unavailable: it loops the strip until stable
  // (kills reassembled tags) and removes any unterminated trailing "<...".
  private stripTags(s: string): string {
    const input = s || '';
    try {
      return new DOMParser().parseFromString(input, 'text/html').body.textContent || '';
    } catch {
      let prev: string;
      let out = input;
      do { prev = out; out = out.replace(/<[^>]*>/g, ''); } while (out !== prev);
      return out.replace(/<[^>]*$/g, '');
    }
  }

  private buildGuidanceHtml(helpText: string): string {
    const raw = (helpText || '').trim();
    if (!raw) return '';
    // The model may emit real HTML (full styling, its choice) or lightweight markdown.
    return this.looksLikeHtml(raw) ? this.sanitizeHtml(raw) : this.formatRichHtml(raw);
  }

  private applyGuidance(fq: FormQuestions, helpText?: string): void {
    const raw = (helpText || '').trim();
    if (!raw) return;
    const plain = this.looksLikeHtml(raw)
      ? this.htmlToPlainText(raw)
      : this.stripInlineMarkup(raw).replace(/\r?\n/g, ' ').replace(/\s+/g, ' ').trim();
    fq.GuidanceText = `[vertical]:::${plain}`;
    fq.GuidanceHtml = this.buildGuidanceHtml(raw);
    fq.DisplayHelp = false;
  }

  // ============================================================
  // PRIVATE: Generate QuestionHtml (label)
  // ============================================================

  private generateQuestionHtml(field: FormQuestions): string {
    const raw = String(field.Label ?? '');
    // Labels render like every other innerHTML surface: real HTML the model authored is
    // sanitized; markdown/plain is converted inline (bold/italic/underline/links). This
    // is universal — the model may style any label, safely — the same pipeline used for
    // guidance and content pages.
    const inner = this.looksLikeHtml(raw)
      ? this.sanitizeHtml(raw)
      : `<font face="Arial">${this.inlineMarkup(raw)}</font>`;
    return `<div id="label_${this.generateUUID()}" maxlength="40">${inner}</div>`;
  }

  // ============================================================
  // PRIVATE: Generate AnswerHtml (matches form-builder.component.ts)
  // ============================================================

  private generateAnswerHtml(field: FormQuestions): string | null {
    const id = field.UniqueID;

    const expr = field.Expression;   // data-expression drives validation binding (999 = none)

    switch (field.Type) {
      case 'TextInput':
        // Byte-for-byte the manual builder's TextInput markup (incl. data-expression).
        return `<div style='text-align:left'>\n        <input type='text' id='textboxID_${id}' data-expression='${expr}'  ></input>\n        </div><br>\n        `;

      case 'TextArea':
        return `<div style='text-align:left'><textarea id="textareaID_${id}" data-expression='${expr}' rows="4" cols="50"></textarea></div><br>`;

      case 'RadioButton': {
        const radioName = 'Radio_' + id;
        let html = '<div style="text-align:left">';
        for (const opt of field.Options) {
          html += '<div><label style="padding:0px 10px">'
            + `<input value="${opt.UniqueID}" data-expression='${expr}' style="margin-right:5px" type="radio" name="${radioName}">`
            + opt.OptionText
            + '</label></div>';
        }
        html += '</div>';
        return html;
      }

      case 'CheckBox': {
        let html = `<div id="Checkbox_WRAPPER_${id}" style="text-align:left">`;
        field.Options.forEach((opt, idx) => {
          html += `<div id="Checkbox_Container${idx}_${id}">`
            + `<label id="Checkbox_Label${idx}_${id}" style="padding:0px 10px">`
            + `<input type="checkbox" data-expression='${expr}' style="margin-right:5px" id="Checkbox_${opt.UniqueID}">`
            + opt.OptionText
            + '</label></div>';
        });
        html += '</div>';
        return html;
      }

      case 'DropDownListSelection': {
        let html = `<div style="display:flex;justify-content:left;">`
          + `<select id="Dropdown_${id}" data-expression='${expr}' style="padding: 5px 10px;width: 200px;outline:none;border-radius: 5px;">`
          + `<option value="">Select option</option>`;
        for (const opt of field.Options) {
          html += `<option value="${opt.UniqueID}">${opt.OptionText}</option>`;
        }
        html += '</select></div>';
        return html;
      }

      case 'Date':
        return `<div style="text-align:left">`
          + `<input type="date" id="${id}" data-expression='${expr}' `
          + `onfocus="this.showPicker && this.showPicker()" `
          + `onkeydown="this.showPicker && this.showPicker(); return false;" `
          + `onpaste="return false" `
          + `style="background-color: white; cursor: pointer;" />`
          + `</div>`;

      case 'Attachment':
        return `<div style='text-align:left'><input type="file" id="fileID_${id}" data-expression='${expr}'/></div><br>`;

      default:
        return null;
    }
  }

  // ============================================================
  // PRIVATE: Generate conditional logic from rules + built sections
  // ============================================================

  /**
   * Rewrite each conditional rule's field references (sourceFieldName / targetFieldName /
   * mandatoryDeps) to the ACTUAL field name when they differ only by formatting. The LLM
   * is not always consistent between how it names a field and how it references it in a
   * rule (case, underscores, a "q"/"status" affix), and generateConditionalLogic matches
   * by EXACT name — so an unreconciled reference silently fails and the dependant never
   * hides. Matching is by a normalized key (lowercased, alphanumerics only); if that's
   * ambiguous we leave the reference untouched (never guess).
   */
  private reconcileRuleNames(sections: ParsedSection[], rules: ParsedConditionalRule[]): void {
    if (!rules?.length) return;
    const norm = (s: string) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    const exact = new Set<string>();
    const byNorm = new Map<string, string[]>();
    for (const s of sections) {
      for (const f of s.fields) {
        exact.add(f.name);
        const k = norm(f.name);
        if (!k) continue;
        (byNorm.get(k) ?? byNorm.set(k, []).get(k)!).push(f.name);
      }
    }
    const resolve = (ref: string): string => {
      if (!ref || exact.has(ref)) return ref;          // already an exact field name
      const hits = byNorm.get(norm(ref));
      return hits && hits.length === 1 ? hits[0] : ref; // unique normalized match only
    };
    for (const r of rules) {
      r.sourceFieldName = resolve(r.sourceFieldName);
      r.targetFieldName = resolve(r.targetFieldName);
      if (r.mandatoryDeps?.length) r.mandatoryDeps = r.mandatoryDeps.map(resolve);
    }
  }

  private generateConditionalLogic(
    templateId: string,
    builtSections: SectionInfo[],
    rules: ParsedConditionalRule[],
    hiddenNames: Set<string> = new Set()
  ): FormConditionalLogic {

    // ── Initial visibility control ─────────────────────────────────────────
    // The buyer-preview shows a question on load iff its CHART NODE is
    // `mandatory: true` (it calls makeNodesRenderOnListItemsUI over the mandatory
    // nodes) and reveals the rest only when a parent answer lists them in its
    // navigate.mandatoryNodes. So initial visibility is driven purely by the
    // chart node's `mandatory` flag (set further below). We take the authoritative
    // initial-visible set straight from the source workbook's row-hidden state
    // (`hiddenNames`): a row hidden in the macro starts hidden here too, a visible
    // row shows on load. This is decoupled from `field.Required` (answer
    // validation) and matches whatever the form author encoded — no heuristics.

    // Build lookup: fieldName → { fieldUUID, sectionUUID, options }
    const fieldLookup = new Map<string, {
      fieldUUID: string;
      sectionUUID: string;
      options: { text: string; uuid: string }[];
      displayOrder: number;
      name: string;
    }>();

    // Also maintain a flat ordered list of every field across every section so we can
    // chain Q1a → Q1b → Q2a → … when no explicit conditional rule exists.
    const flatFieldOrder: { name: string; field: FormQuestions; section: SectionInfo }[] = [];

    // Content-only blocks (GuidanceText/TextLabel) are display text, not answerable
    // questions. They must be INERT in the conditional-logic graph — never chained,
    // never a reveal source/target/link — or the FormService's recursive graph walk
    // can choke on the answer-less node (an uncatchable crash that kills the process).
    // Keep them in fieldLookup (for UUID) but OUT of the flow order and all edges.
    // Content blocks are BUILT with Type 'TextArea' + a __contentBlock flag + IsMappable=false
    // (see field build, ~line 3020) — NOT Type 'Info'. So detect them by those persisted
    // signals, not the Type string, or the intro slips into the regular-question path and
    // gets marked Mandatory (blocking supplier submit).
    const isContentBlock = (f: any) =>
      f?.__contentBlock === true || f?.IsMappable === false ||
      f?.Type === 'Info' || f?.Type === 'GuidanceText' || f?.Type === 'TextLabel';
    const contentBlockUuids = new Set<string>();

    for (const section of builtSections) {
      for (const field of section.Fields) {
        fieldLookup.set(field.Name, {
          fieldUUID: field.UniqueID,
          sectionUUID: section.UniqueID,
          options: field.Options.map(o => ({ text: o.OptionText, uuid: o.UniqueID })),
          displayOrder: field.DisplayOrder,
          name: field.Name
        });
        if (isContentBlock(field)) { contentBlockUuids.add(field.UniqueID); continue; }
        flatFieldOrder.push({ name: field.Name, field, section });
      }
    }

    // Tolerant field resolution. The AI's conditional-rule names can differ from the
    // built field Name by case / separators / affix ("Q1a" vs "Q1_a"). With EXACT
    // matching, the source-rule filter and the dependant lookups miss, so
    // MandatoryDependantQuestion comes out EMPTY and the Logic Builder shows no
    // dependent questions. Normalized index (unique keys only — an ambiguous norm maps
    // to null so we never match the wrong field), mirroring reconcileRuleNames.
    const normName = (s: string) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    const normToName = new Map<string, string | null>();
    for (const nm of fieldLookup.keys()) {
      const k = normName(nm);
      if (!k) continue;
      normToName.set(k, normToName.has(k) ? null : nm);
    }
    const resolveField = (ref: string) => {
      if (fieldLookup.has(ref)) return fieldLookup.get(ref)!;
      const exact = normToName.get(normName(ref));
      return exact ? fieldLookup.get(exact)! : undefined;
    };

    // Find the next field after `currentName` in flat order, skipping `_Comments`
    // textareas (those are dependants of the question above them, not stops in the flow).
    const nextFieldInFlow = (currentName: string): typeof flatFieldOrder[number] | null => {
      const idx = flatFieldOrder.findIndex(f => f.name === currentName);
      for (let j = idx + 1; j < flatFieldOrder.length; j++) {
        if (flatFieldOrder[j].name.endsWith('_Comments')) continue;
        return flatFieldOrder[j];
      }
      return null;
    };

    // Build SectionConditions — one entry per section
    const sectionConditions = builtSections.map(section => {
      const logicEntries: any[] = [];

      for (const field of section.Fields) {
        const info = fieldLookup.get(field.Name)!;

        // Content block (Info / GuidanceText / TextLabel): display text, NOT a question.
        // Emit an inert node with Mandatory = FALSE — otherwise the runtime treats a
        // mandatory node as "must be answered" and, since a content block has no answer,
        // it blocks the supplier from submitting (the yellow "Mandatory question to be
        // answered" dot on the intro). It still renders: showFieldOnPreviewArea() shows
        // non-mandatory fields via the isFieldOptional() path — it just isn't a mandatory
        // nav-list question and isn't required to submit.
        if (isContentBlock(field)) {
          logicEntries.push({
            SourceQuestinID: info.fieldUUID,
            ConditionsExist: false,
            Mandatory: false,
            Name: field.Name,
            AllowScoring: false,
            DisplayOrder: info.displayOrder,
            Conditions: []
          });
          continue;
        }

        // Check if this field has any conditional rules as source (name-tolerant match).
        const rulesForField = rules.filter(r => normName(r.sourceFieldName) === normName(field.Name));

        if (rulesForField.length > 0) {
          const conditions: any[] = [];

          // Default dependent set — derived from naming conventions when the
          // rule doesn't carry an explicit `mandatoryDeps` list. Used by legacy
          // Pattern A (Q15a → Q15a_Comments) and legacy Pattern C (Q1 → Q1a, Q1b, …).
          // CCRA-style rules supply their own `mandatoryDeps` (which can include
          // controls or sub-letter siblings beyond what naming alone would yield).
          const defaultDependentChildUuids: string[] = [];
          {
            const parentName = field.Name;
            const commentsName = `${parentName}_Comments`;
            const commentsInfo = fieldLookup.get(commentsName);
            if (commentsInfo) defaultDependentChildUuids.push(commentsInfo.fieldUUID);

            const numOnly = parentName.match(/^Q(\d+(?:[.\-]\d+)*)$/);
            if (numOnly) {
              const childPat = new RegExp(`^Q${this.escapeRegex(numOnly[1])}[a-z]+$`);
              for (const f of flatFieldOrder) {
                if (childPat.test(f.name)) {
                  const ci = fieldLookup.get(f.name);
                  if (ci) defaultDependentChildUuids.push(ci.fieldUUID);
                }
              }
            }
          }

          // Translate an explicit `mandatoryDeps` (array of field names) into the
          // corresponding UUID list. Names that don't resolve (deleted / renamed)
          // are skipped.
          // A dependant must be a HIDDEN field — something revealed later. A field
          // visible in the source shows on load (it's mandatory) and must never
          // appear in MandatoryDependantQuestion (e.g. the Job form's controls are
          // visible, so they're NOT dependants even though they sit after a
          // gateway). Filter visible names out when we have hidden-row info.
          const filterToHidden = hiddenNames.size > 0;
          const depsFromRule = (rule: ParsedConditionalRule): string[] => {
            if (!rule.mandatoryDeps?.length) return [...defaultDependentChildUuids];
            const out: string[] = [];
            for (const n of rule.mandatoryDeps) {
              const inf = resolveField(n);
              if (!inf) continue;
              // hidden-state check uses the RESOLVED field name, not the raw rule name.
              if (filterToHidden && !hiddenNames.has(inf.name)) continue;
              if (!contentBlockUuids.has(inf.fieldUUID)) out.push(inf.fieldUUID);
            }
            return out;
          };

          // Emit ONE condition per option of the source field — exactly like the
          // manual Logic Builder (a Yes/No gate carries BOTH a "Yes" and a "No"
          // condition). Dependants attach only to the option(s) whose value triggers
          // them; every other option carries an empty dependant set. This is what
          // lets the runtime HIDE the dependants again when the answer switches to a
          // non-trigger value. EdgeRefID is a fresh GUID per condition (reused as the
          // chart link's conditionID), NOT the option UUID — mirroring the payload.
          const nextForBranch = nextFieldInFlow(field.Name);
          const nextBranchUuid = nextForBranch ? (fieldLookup.get(nextForBranch.name)?.fieldUUID ?? '') : '';

          // The set this source reveals on its trigger (e.g. Q1 → Q1a/Q1b/Q1c), and the
          // "skip" destination that jumps PAST them to the next real field (Q2). A NON-
          // trigger option (e.g. "No") must navigate to that skip target — not to the
          // immediate next field, which is a hidden sub-question. Without this, both "Yes"
          // and "No" route into Q1a even though the document says only "Yes" reveals it.
          const revealUuids = new Set<string>();
          for (const rule of rulesForField) for (const d of depsFromRule(rule)) revealUuids.add(d);
          let skipDestUuid = '';
          if (revealUuids.size > 0) {
            const srcIdx = flatFieldOrder.findIndex(f => f.name === field.Name);
            for (let j = srcIdx + 1; j < flatFieldOrder.length; j++) {
              if (flatFieldOrder[j].name.endsWith('_Comments')) continue;
              const u = fieldLookup.get(flatFieldOrder[j].name)?.fieldUUID;
              if (u && revealUuids.has(u)) continue;   // skip the revealed sub-questions
              skipDestUuid = u ?? '';
              break;
            }
          }

          for (const opt of info.options) {
            const optNorm = (opt.text || '').trim().toLowerCase();
            let destination = nextBranchUuid;
            const deps: string[] = [];
            let matched = false;

            for (const rule of rulesForField) {
              const triggerNorm = (rule.triggerValue || '').trim().toLowerCase();
              const matches = rule.revealOnAnyAnswer
                || optNorm === triggerNorm
                || (triggerNorm.length > 0 && optNorm.includes(triggerNorm));
              if (!matches) continue;
              matched = true;

              const targetInfo = resolveField(rule.targetFieldName);
              if (targetInfo && !contentBlockUuids.has(targetInfo.fieldUUID)) destination = targetInfo.fieldUUID;
              for (const d of depsFromRule(rule)) {
                if (d && !deps.includes(d)) deps.push(d);
              }
            }

            // Non-trigger option: skip past this source's revealed block (Q1=No → Q2).
            if (!matched && revealUuids.size > 0 && skipDestUuid) destination = skipDestUuid;

            conditions.push({
              UniqueID: this.generateUUID(),
              EdgeRefID: this.generateUUID(),   // fresh per-condition id (= chart link conditionID)
              EdgeValue: opt.text,
              DestinationQuestionID: destination,
              MandatoryDependantQuestion: deps,
              Score: 0
            });
          }

          // A source is force-marked mandatory (so it shows on load and can be
          // answered to drive its branch) ONLY when it is itself visible. A source
          // that is hidden until revealed (e.g. CUI3, Risk9 — they gate their own
          // children yet start collapsed) must STAY non-mandatory, or it would
          // show on load. `hiddenNames` is the source row-hidden state.
          if (!hiddenNames.has(field.Name)) field.Required = true;
          logicEntries.push({
            SourceQuestinID: info.fieldUUID,
            ConditionsExist: true,
            Mandatory: !hiddenNames.has(field.Name),
            Name: field.Name,
            AllowScoring: false,
            DisplayOrder: info.displayOrder,
            Conditions: conditions
          });
        } else {
          // ── Linear-chain fallback ──
          // No explicit rule for this field → wire it to the NEXT field in the flat
          // order so the form has an explicit Q1a → Q1b → Q2a → … chain rather than
          // an empty conditional graph.
          //
          //   • If the field has options (radio / dropdown / checkbox), emit one
          //     condition per option, each routing to the same next field. This means
          //     "no matter what you pick, the next question is X".
          //   • If the field has no options (text / textarea / date / file), emit a
          //     single condition with empty Edge values acting as the default-next.
          //
          //   `_Comments` fields are skipped as destinations because they're
          //   dependants of their parent radio, not stops in the main flow.
          const nextLink = nextFieldInFlow(field.Name);
          const linearConditions: any[] = [];

          if (nextLink && !field.Name.endsWith('_Comments')) {
            const nextInfo = fieldLookup.get(nextLink.name)!;

            if (info.options.length > 0) {
              for (const opt of info.options) {
                linearConditions.push({
                  UniqueID: this.generateUUID(),
                  EdgeRefID: opt.uuid,
                  EdgeValue: opt.text,
                  DestinationQuestionID: nextInfo.fieldUUID,
                  MandatoryDependantQuestion: [],
                  Score: 0
                });
              }
            } else {
              linearConditions.push({
                UniqueID: this.generateUUID(),
                EdgeRefID: 'EMPTY',   // manual Logic Builder uses the literal "EMPTY" for linear nav
                EdgeValue: '',
                DestinationQuestionID: nextInfo.fieldUUID,
                MandatoryDependantQuestion: [],
                Score: 0
              });
            }
          }

          // A field with no outgoing rules still drives its own initial
          // visibility: it is mandatory (shown on load) IFF its source row is
          // visible. Without this, a visible question that happens to gate
          // nothing (e.g. CCRA SI5 — only ever a "No→ skip" target) would be
          // emitted Mandatory:false and never render on load, even though the
          // chart node says mandatory:true. Mirror the source-field rule above.
          if (!hiddenNames.has(field.Name)) field.Required = true;
          logicEntries.push({
            SourceQuestinID: info.fieldUUID,
            ConditionsExist: false,   // linear nav is not a branch — matches manual Logic Builder
            Mandatory: !hiddenNames.has(field.Name),
            Name: field.Name,
            AllowScoring: false,
            DisplayOrder: info.displayOrder,
            Conditions: linearConditions
          });
        }
      }

      return {
        SectionUniqueID: section.UniqueID,
        UniqueID: section.UniqueID,       // some code paths use UniqueID instead of SectionUniqueID
        ConditionalLogic: logicEntries
      };
    });

    // Prepend a START entry to the first section — mirrors the manual Logic Builder
    // output (SourceQuestinID "1" → first field, EdgeRefID "EMPTY", Mandatory false).
    if (sectionConditions.length > 0) {
      const startTarget = flatFieldOrder.find(f => !f.name.endsWith('_Comments')) ?? flatFieldOrder[0];
      if (startTarget) {
        (sectionConditions[0].ConditionalLogic as any[]).unshift({
          SourceQuestinID: '1',
          ConditionsExist: false,
          Mandatory: false,
          Name: '',
          AllowScoring: false,
          DisplayOrder: 1,
          Conditions: [{
            UniqueID: this.generateUUID(),
            EdgeRefID: 'EMPTY',
            EdgeValue: '',
            DestinationQuestionID: fieldLookup.get(startTarget.name)!.fieldUUID,
            MandatoryDependantQuestion: [],
            Score: 0
          }]
        });
      }
    }

    // ── Dependent questions are never mandatory ────────────────────────────
    // Any question that appears in a condition's MandatoryDependantQuestion is a
    // conditionally-revealed dependant — it must NOT be a required/mandatory
    // field (it starts hidden and may never be shown). Collect every dependant
    // UUID actually emitted above and clear Required on those fields. (Its chart
    // node `mandatory` flag — initial visibility — is handled separately from the
    // source row-hidden state.)
    const dependantUuids = new Set<string>();
    for (const sc of sectionConditions) {
      for (const entry of sc.ConditionalLogic as any[]) {
        for (const cond of entry.Conditions) {
          for (const dep of (cond.MandatoryDependantQuestion || [])) {
            if (dep) dependantUuids.add(dep);
          }
        }
      }
    }
    for (const section of builtSections) {
      for (const fq of section.Fields) {
        if (dependantUuids.has(fq.UniqueID)) fq.Required = false;
      }
    }

    // ── Build ChartJson (Nodes + Links) so the conditional-logic graph view actually
    //    renders something after import. Without this, questions.component.ts shows
    //    only the implicit START node and no edges.
    //
    //    Node shape mirrors questions.component.ts:
    //      { id, label, mandatory, condition, scoring }
    //    Edge shape:
    //      { source, target, label, conditionID, data: [] }
    const chartNodes: any[] = [
      { id: '1', label: 'START', mandatory: '', condition: '', scoring: '' }
    ];
    const chartLinks: any[] = [];
    const seenEdges = new Set<string>();

    // Add every field as a node, in flat order. Prefer the short field Name
    // (FCI1, CUI2a, 3.1.1, Q5_Comments) as the chart label — Labels are the full
    // question text which makes nodes unreadable in the graph view. Fall back to
    // the Label only when Name is empty.
    for (const f of flatFieldOrder) {
      const info = fieldLookup.get(f.name)!;
      chartNodes.push({
        id: info.fieldUUID,
        label: f.name || f.field.Label,
        // `mandatory` drives initial visibility in the buyer-preview: mandatory
        // nodes show on load, the rest are revealed by their parent. We take this
        // straight from the source workbook's row-hidden state — a row hidden in
        // the macro is hidden here (mandatory:false), a visible row shows.
        mandatory: hiddenNames.has(f.name) ? 'false' : 'true',
        condition: 'false',
        scoring: 'false'
      });
    }

    // Content blocks (Info / intro) are display text, not questions — do NOT add them as
    // chart nodes. The Logic Builder (questions.component) renders its graph AND its
    // left-hand node list from ChartJson.Nodes, so a content node shows up there as a
    // stray, unconnected box. Omitting it keeps the intro out of the Logic Builder; it
    // still renders in the form (buyer-preview shows non-mandatory fields via the
    // isFieldOptional path) and its inert SectionConditions entry remains for FormService.

    // START → first non-comments field
    const firstField = flatFieldOrder.find(f => !f.name.endsWith('_Comments'));
    if (firstField) {
      const firstInfo = fieldLookup.get(firstField.name)!;
      chartLinks.push({
        source: '1',
        target: firstInfo.fieldUUID,
        label: '',
        conditionID: this.generateUUID(),
        data: []
      });
    }

    // Walk every Conditions[] we just built and emit a link for each
    for (const sc of sectionConditions) {
      for (const cl of sc.ConditionalLogic as any[]) {
        for (const cond of cl.Conditions) {
          const key = `${cl.SourceQuestinID}->${cond.DestinationQuestionID}:${cond.EdgeValue || ''}`;
          if (seenEdges.has(key)) continue;
          seenEdges.add(key);
          chartLinks.push({
            source: cl.SourceQuestinID,
            target: cond.DestinationQuestionID,
            label: cond.EdgeValue || '',
            // manual uses the condition's EdgeRefID as the link conditionID (null for linear)
            conditionID: cond.EdgeRefID && cond.EdgeRefID !== 'EMPTY' ? cond.EdgeRefID : null,
            data: []
          });
        }
      }
    }

    const chartJson = JSON.stringify([{ Nodes: chartNodes, Links: chartLinks }]);

    return {
      TemplateID: templateId,
      ChartJson: chartJson,
      SectionConditions: sectionConditions
    } as FormConditionalLogic;
  }

  // ============================================================
  // HELPER: Detect expression ID from field label
  // ============================================================

  private detectExpression(label: string): number {
    const lower = label.toLowerCase();
    if (lower.includes('email'))  return EXPRESSION_MAP['email'];
    if (lower.includes('phone'))  return EXPRESSION_MAP['phone'];
    return 0;
  }

  // ============================================================
  // HELPER: Parse question text (free-form format)
  // ============================================================

  private parseQuestionText(text: string): {
    type: string; label: string; required: boolean; options: string[]; expression: number;
  } {
    // Required when text contains any of:
    //   "(*)"      "Field (*)"        ← common in Excel/Word
    //   "*" end    "Field *"          ← trailing
    //   "*" start  "*Field"           ← CRA-4 PDF style
    //   "(required)"                   ← explicit
    const required = text.includes('(*)')
      || /\*\s*$/.test(text)
      || /^\s*\*/.test(text)
      || /\(\s*required\s*\)/i.test(text);

    // Detect trailing option text in the question itself (PDF text extraction often joins
    // the question label and the radio-button labels onto one line).
    const trailingYesNo  = /\s+Yes\s+No\s*$/i.test(text) || /\s+No\s+Yes\s*$/i.test(text);
    const trailingTF     = /\s+True\s+False\s*$/i.test(text) || /\s+False\s+True\s*$/i.test(text);

    // Strip type hints and markers from label
    let label = text
      .replace(/\(\*\)/g, '')
      .replace(/\s*\*\s*$/, '')
      .replace(/^\*\s*/, '')
      .replace(/\s*[–-]\s*(Text Entry|Text Input|Dropdown Selection|Drop Down|Radio Button|Numeric Entry|Date Entry|Long Text|File Upload|Attachment|Check\s*Box).*/i, '')
      .replace(/\s*&\s*Comments\s*$/i, '')
      .replace(/\s*\(\s*Yes\s*\/\s*No\s*\)/i, '')
      .replace(/\s*\(\s*True\s*\/\s*False\s*\)/i, '')
      .replace(/\s*\(\s*required\s*\)/i, '')
      .replace(/\s+Yes\s+No\s*$/i, '')
      .replace(/\s+No\s+Yes\s*$/i, '')
      .replace(/\s+True\s+False\s*$/i, '')
      .replace(/\s+False\s+True\s*$/i, '')
      .trim();

    let type = 'textbox';
    let options: string[] = [];
    let expression = 0;

    // --- 1. Explicit type hints (e.g., "– Radio Button", "– Dropdown") ---
    if (/radio\s*button/i.test(text) || /[-–]\s*radio/i.test(text)) {
      type = 'radio';
      options = /true.*false/i.test(text) ? ['True', 'False'] : ['Yes', 'No'];
    } else if (/check\s*box/i.test(text) || /[-–]\s*check/i.test(text)) {
      type = 'checkbox';
      options = /true.*false/i.test(text) ? ['True', 'False'] : ['Yes', 'No'];
    } else if (/dropdown|drop\s*down/i.test(text) || /[-–]\s*dropdown/i.test(text) || /[-–]\s*selection/i.test(text)) {
      type = 'dropdown';
      options = /yes\s*\/?\s*no/i.test(text) ? ['Yes', 'No'] : [];
    } else if (/file\s*upload|attachment|upload\s*file|upload\s*document|attach\s/i.test(text) || /[-–]\s*attach/i.test(text)) {
      type = 'fileupload';
    } else if (/date\s*entry|date\s*picker/i.test(text) || /[-–]\s*date/i.test(text)) {
      type = 'date';
    } else if (/numeric\s*entry/i.test(text) || /[-–]\s*numeric/i.test(text)) {
      type = 'textbox';
      expression = 6;
    } else if (/long\s*text|text\s*area|textarea|multi.?line/i.test(text) || /[-–]\s*text\s*area/i.test(text)) {
      type = 'textarea';

    // --- 2. Inline options: "(Yes/No)", "(True/False)", "Yes / No", trailing "Yes No" ---
    } else if (/true\s*\/\s*false/i.test(text)) {
      type = 'radio';
      options = ['True', 'False'];
    } else if (/\(\s*yes\s*\/\s*no\s*\)/i.test(text) || /\byes\s*\/\s*no\b/i.test(text)) {
      type = 'radio';
      options = ['Yes', 'No'];
    } else if (trailingTF) {
      type = 'radio';
      options = ['True', 'False'];
    } else if (trailingYesNo) {
      type = 'radio';
      options = ['Yes', 'No'];

    // --- 3. Contextual inference from label content ---
    } else if (/\bdate\b/i.test(label) && !/\bup\s*to\s*date\b/i.test(label) && !/\bto\s*date\b/i.test(label)) {
      type = 'date';
    } else if (/\bupload\b|\battach(?:ment|ed)?\b|\bsupporting\s*document/i.test(label)) {
      type = 'fileupload';
    } else if (/\bdescri(?:be|ption)\b|\bexplain\b|\bcomments?\b|\bprovide\s*details?\b|\badditional\s*info/i.test(label)) {
      type = 'textarea';
    } else if (/\bnumeric\b|\bnumber\s*of\b|\bhow\s*many\b|\bquantity\b|\btotal\b|\bamount\b/i.test(label)) {
      type = 'textbox';
      expression = 6;
    }

    // Extract inline options like "(Option A / Option B / Option C)" — but skip
    // date format markers like "(DD/MM/YYYY)" or "(MM/DD/YYYY)" which look the same.
    if (type === 'textbox' || type === 'dropdown') {
      const inlineOptions = text.match(/\(([^)]+\/[^)]+)\)/);
      if (inlineOptions) {
        const inner = inlineOptions[1].trim();
        const looksLikeDateFormat = /^[YMD]{1,4}\s*\/\s*[YMD]{1,4}(\s*\/\s*[YMD]{1,4})?$/i.test(inner);
        if (!looksLikeDateFormat) {
          const parsed = inner.split(/\s*\/\s*/).map(o => o.trim()).filter(o => o);
          if (parsed.length >= 2 && parsed.length <= 10) {
            type = parsed.length <= 3 ? 'radio' : 'dropdown';
            options = parsed;
            label = label.replace(/\s*\([^)]+\/[^)]+\)\s*/, ' ').trim();
          }
        } else {
          // Date marker → force date type and strip the marker from the label
          type = 'date';
          label = label.replace(/\s*\(\s*[YMD]{1,4}\s*\/\s*[YMD]{1,4}(\s*\/\s*[YMD]{1,4})?\s*\)\s*/gi, ' ').trim();
        }
      }
    }

    // Detect expression from label
    if (!expression) {
      expression = this.detectExpression(label);
    }

    return { type, label, required, options, expression };
  }

  // ============================================================
  // HELPERS
  // ============================================================

  private findColumn(headers: string[], options: string[]): number {
    for (const opt of options) {
      const idx = headers.indexOf(opt);
      if (idx !== -1) return idx;
    }
    return -1;
  }

  // Like findColumn but also accepts partial matches (header CONTAINS option).
  // Used for non-standard structured Excel headers like "Scoping Question",
  // "Response Options", "Question Identifier" etc.
  private findColumnPartial(headers: string[], options: string[]): number {
    // Try exact match first (most specific wins)
    for (const opt of options) {
      const idx = headers.indexOf(opt);
      if (idx !== -1) return idx;
    }
    // Then partial match — headers[i].includes(option) — first option wins
    for (const opt of options) {
      const idx = headers.findIndex(h => h.includes(opt));
      if (idx !== -1) return idx;
    }
    return -1;
  }

  private parseRequired(value: any): boolean {
    if (!value) return false;
    const str = String(value).toLowerCase().trim();
    return ['yes', 'true', '1', '*', 'required', 'mandatory'].includes(str);
  }

  private isSectionHeader(text: string): boolean {
    if (!text || text.length > 100) return false;
    if (text.startsWith('(')) return false;
    if (/^[\d]+(?:[.\-][\d]+)*\s*[\.\)]/.test(text)) return false;
    const keywords = ['information', ' questions', 'section', 'details', ' level'];
    return keywords.some(kw => text.toLowerCase().includes(kw));
  }

  private escapeHtml(s: any): string {
    return String(s ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private generateUUID(): string {
    // CodeQL (js/insecure-randomness) flags Math.random for any id used in a
    // security-adjacent context. The UUIDs we mint here are field/section IDs,
    // not session tokens, but switching to the platform crypto generator is the
    // right fix and produces the same v4 UUID shape. crypto / getRandomValues
    // are universally available in modern browsers and Node.
    const c: any = typeof crypto !== 'undefined' ? crypto : null;
    if (c?.randomUUID) return c.randomUUID();
    const bytes = new Uint8Array(16);
    c.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;   // version 4
    bytes[8] = (bytes[8] & 0x3f) | 0x80;   // variant 10
    const h = Array.from(bytes, b => b.toString(16).padStart(2, '0'));
    return `${h[0]}${h[1]}${h[2]}${h[3]}-${h[4]}${h[5]}-${h[6]}${h[7]}-${h[8]}${h[9]}-${h[10]}${h[11]}${h[12]}${h[13]}${h[14]}${h[15]}`;
  }

  // Escape every regex meta-character (including backslash) so the input can
  // be safely interpolated into a RegExp. The previous one-off `replace(/[.]/g,
  // '\\.')` only escaped dots and left backslashes untouched — CodeQL
  // (js/incomplete-sanitization) flagged that as incomplete.
  private escapeRegex(s: string): string {
    return s.replace(/[\\^$.*+?()[\]{}|]/g, '\\$&');
  }
}
